import {entries, fromEntries} from './util';

// Central registry of all Wappalyzer technologies.
// This is the single source of truth for:
// 1. The WappTech type (all known wapp slugs)
// 2. Auto-generated stack signal labels (for ScoringSignalsDefinition)
// 3. Auto-generated signal eval functions (for CustomerFacingSignals)
//
// Entry types:
// - {signalKey, label}: auto-generates a scoring signal and eval function
// - {manualOverride: true}: has custom eval logic in customerSignals.ts
// - null: tech detection only, no scoring signal

type WappSignalEntry =
  | {signalKey: string; label: string}
  | {manualOverride: true}
  | null;

export const WappStackSignals = {
  // --- technologies with scoring signals ---
  '1c-bitrix': {signalKey: '1cBitrix', label: '1C-Bitrix'},
  '33across': {signalKey: '33across', label: '33Across'},
  '6sense': {signalKey: 'sixSense', label: '6sense'},
  'ab-tasty': {manualOverride: true},
  accessibe: {signalKey: 'accessibe', label: 'accessiBe'},
  'acquia-cloud-platform': {
    signalKey: 'acquiaCloudPlatform',
    label: 'Acquia Cloud Platform',
  },
  activecampaign: {signalKey: 'activeCampaign', label: 'Active Campaign'},
  ada: {signalKey: 'adaSupport', label: 'Ada Support'},
  addsearch: {signalKey: 'addSearch', label: 'AddSearch'},
  'adobe-analytics': {signalKey: 'adobeAnalytics', label: 'Adobe Analytics'},
  'adobe-audience-manager': {
    signalKey: 'adobeAudienceManager',
    label: 'Adobe Audience Manager',
  },
  'adobe-experience-manager': {
    signalKey: 'adobeExperienceManager',
    label: 'Adobe Experience Manager',
  },
  'adobe-target': {signalKey: 'adobeTarget', label: 'Adobe Target Standard'},
  adroll: {signalKey: 'adRoll', label: 'AdRoll'},
  ahrefs: {signalKey: 'ahrefs', label: 'Ahrefs'},
  akamai: {signalKey: 'akamai', label: 'Akamai'},
  algolia: {signalKey: 'algolia', label: 'Algolia'},
  'amazon-advertising': {
    signalKey: 'amazonAdvertising',
    label: 'Amazon Advertising',
  },
  'amazon-cloudfront': {signalKey: 'cloudFront', label: 'Amazon CloudFront'},
  'amazon-pay': {signalKey: 'amazonPay', label: 'Amazon Pay'},
  'amazon-s3': {signalKey: 'amazonS3', label: 'Amazon S3'},
  'amazon-ses': {signalKey: 'amazonSes', label: 'Amazon Simple Email Service'},
  'amazon-web-services': {manualOverride: true},
  ambassador: {signalKey: 'ambassador', label: 'Ambassador'},
  amplitude: {signalKey: 'amplitude', label: 'Amplitude'},
  angular: {signalKey: 'angular', label: 'Angular'},
  apollo: {signalKey: 'apollo', label: 'Apollo'},
  appcues: {signalKey: 'appcues', label: 'Appcues'},
  asana: {manualOverride: true},
  asknicely: {signalKey: 'askNicely', label: 'AskNicely'},
  'atlassian-bitbucket': {manualOverride: true},
  'atlassian-confluence': {manualOverride: true},
  'atlassian-jira': {manualOverride: true},
  attentive: {signalKey: 'attentive', label: 'Attentive'},
  autopilot: {signalKey: 'autopilot', label: 'Autopilot'},
  awin: {signalKey: 'awin', label: 'AWIN'},
  azure: {manualOverride: true},
  'basis-technologies': {
    signalKey: 'basisTechnologies',
    label: 'Basis Technologies',
  },
  'bazaarvoice-reviews': {
    signalKey: 'bazaarVoice',
    label: 'Bazaarvoice Reviews',
  },
  bigcommerce: {signalKey: 'bigCommerce', label: 'BigCommerce'},
  bloomreach: {signalKey: 'bloomReach', label: 'Bloomreach'},
  'blotout-edgetag': {manualOverride: true},
  blueconic: {manualOverride: true},
  bluecore: {signalKey: 'bluecore', label: 'Bluecore'},
  blueshift: {signalKey: 'blueshift', label: 'Blueshift'},
  braze: {signalKey: 'braze', label: 'Braze'},
  brightcove: {signalKey: 'brightcove', label: 'Brightcove'},
  brightedge: {signalKey: 'brightEdge', label: 'BrightEdge'},
  bronto: {signalKey: 'bronto', label: 'Bronto'},
  callrail: {signalKey: 'callRail', label: 'CallRail'},
  'campaign-monitor': {signalKey: 'campaignMonitor', label: 'Campaign Monitor'},
  'cart-functionality': {
    signalKey: 'cartFunctionaliyt',
    label: 'Cart Functionality',
  },
  chargebee: {signalKey: 'chargebee', label: 'Chargebee'},
  'chili-piper': {signalKey: 'chiliPiper', label: 'Chili Piper'},
  chorus: {signalKey: 'chorus', label: 'Chorus'},
  churnzero: {signalKey: 'churnzero', label: 'ChurnZero'},
  'clearbit-reveal': {signalKey: 'clearbitReveal', label: 'Clearbit Reveal'},
  clevertap: {signalKey: 'cleverTap', label: 'CleverTap'},
  clicky: {signalKey: 'clicky', label: 'Clicky'},
  cloudflare: {manualOverride: true},
  cloudinary: {signalKey: 'cloudinary', label: 'Cloudinary'},
  comeet: {signalKey: 'comeet', label: 'Comeet'},
  comscore: {signalKey: 'comScore', label: 'comScore'},
  'constant-contact': {signalKey: 'constantContact', label: 'Constant Contact'},
  contentful: {signalKey: 'contentful', label: 'Contentful'},
  contently: {signalKey: 'contently', label: 'Contently'},
  contentsquare: {signalKey: 'contentsquare', label: 'Contentsquare'},
  coveo: {signalKey: 'coveo', label: 'Coveo'},
  'crazy-egg': {signalKey: 'crazyEgg', label: 'CrazyEgg'},
  'crisp-live-chat': {signalKey: 'crisp', label: 'Crisp'},
  criteo: {signalKey: 'criteo', label: 'Criteo'},
  'customer-io': {signalKey: 'customerIo', label: 'Customer.io'},
  datadome: {signalKey: 'dataDome', label: 'DataDome'},
  delighted: {signalKey: 'delighted', label: 'Delighted'},
  demandbase: {signalKey: 'demandbase', label: 'Demandbase'},
  discourse: {signalKey: 'discourse', label: 'Discourse'},
  docusign: {signalKey: 'docusign', label: 'DocuSign'},
  dreamhost: {signalKey: 'dreamHost', label: 'DreamHost'},
  drift: {signalKey: 'drift', label: 'Drift'},
  drip: {signalKey: 'drip', label: 'Drip'},
  drupal: {signalKey: 'drupal', label: 'Drupal'},
  'dynamic-yield': {signalKey: 'dynamicYield', label: 'Dynamic Yield'},
  elementor: {signalKey: 'elementor', label: 'Elementor'},
  eloqua: {signalKey: 'eloqua', label: 'Eloqua'},
  emarsys: {signalKey: 'emarsys', label: 'Emarsys'},
  engagio: {signalKey: 'engagio', label: 'Engagio'},
  envoy: {signalKey: 'envoy', label: 'Envoy'},
  exponea: {signalKey: 'exponea', label: 'Exponea'},
  'facebook-pixel': {
    signalKey: 'facebookConversionTracking',
    label: 'Facebook Conversion Tracking',
  },
  fastly: {signalKey: 'fastly', label: 'Fastly'},
  firebase: {signalKey: 'firebase', label: 'Firebase'},
  freshteam: {signalKey: 'freshteam', label: 'Freshteam'},
  fullstory: {signalKey: 'fullStory', label: 'FullStory'},
  getfeedback: {signalKey: 'getFeedback', label: 'GetFeedback'},
  'github-pages': {signalKey: 'gitHubHoster', label: 'GitHub Pages'},
  glassbox: {signalKey: 'glassBox', label: 'Glassbox'},
  'google-ads': {manualOverride: true},
  'google-ads-conversion-tracking': {manualOverride: true},
  'google-analytics': {signalKey: 'googleAnalytics', label: 'Google Analytics'},
  'google-cloud-cdn': {signalKey: 'googleCloudCdn', label: 'Google Cloud CDN'},
  'google-optimize': {
    signalKey: 'googleOptimize360',
    label: 'Google Optimize 360',
  },
  'google-pay': {signalKey: 'googlePay', label: 'Google Pay'},
  'google-tag-manager': {
    signalKey: 'googleTagManager',
    label: 'Google Tag Manager',
  },
  'google-workspace': {manualOverride: true},
  heap: {signalKey: 'heap', label: 'Heap'},
  heroku: {signalKey: 'heroku', label: 'Heroku'},
  hostgator: {signalKey: 'hostgator', label: 'Hostgator'},
  hotjar: {signalKey: 'hotjar', label: 'Hotjar'},
  hubspot: {signalKey: 'hubspot', label: 'Hubspot'},
  'hubspot-cms-hub': {signalKey: 'hubspotCmsHub', label: 'HubSpot CMS Hub'},
  impact: {signalKey: 'impact', label: 'Impact'},
  imperva: {signalKey: 'imperva', label: 'Imperva'},
  inmoment: {signalKey: 'inMoment', label: 'InMoment'},
  insided: {manualOverride: true},
  insider: {signalKey: 'insider', label: 'Insider'},
  instapage: {signalKey: 'instapage', label: 'Instapage'},
  intercom: {signalKey: 'intercom', label: 'Intercom'},
  iterable: {signalKey: 'iterable', label: 'Iterable'},
  joomla: {signalKey: 'joomla', label: 'Joomla'},
  keap: {signalKey: 'infusionsoft', label: 'Keap (Infusionsoft)'},
  kissmetrics: {signalKey: 'kissMetrics', label: 'KISSmetrics'},
  klaviyo: {signalKey: 'klaviyo', label: 'Klaviyo'},
  kustomer: {manualOverride: true},
  launchdarkly: {manualOverride: true},
  leadfeeder: {signalKey: 'leadfeeder', label: 'Leadfeeder'},
  leanplum: {signalKey: 'leanplum', label: 'Leanplum'},
  'linkedin-ads': {manualOverride: true},
  'linkedin-insight-tag': {manualOverride: true},
  linkmink: {manualOverride: true},
  listrak: {signalKey: 'listrak', label: 'Listrak'},
  lytics: {manualOverride: true},
  magento: {signalKey: 'magento', label: 'Magento'},
  mailchimp: {signalKey: 'mailChimp', label: 'MailChimp'},
  mailgun: {signalKey: 'mailgun', label: 'Mailgun'},
  mapp: {signalKey: 'mapp', label: 'Mapp (BlueHornet)'},
  marketo: {signalKey: 'marketo', label: 'Adobe Marketo'},
  'microsoft-365': {manualOverride: true},
  'microsoft-advertising': {
    signalKey: 'microsoftAdvertising',
    label: 'Microsoft Advertising',
  },
  'microsoft-dynamics-365-commerce': {manualOverride: true},
  'mighty-network': {manualOverride: true},
  mixpanel: {signalKey: 'mixpanel', label: 'Mixpanel'},
  mntn: {signalKey: 'mntn', label: 'MNTN'},
  moengage: {signalKey: 'moEngage', label: 'MoEngage'},
  monsido: {manualOverride: true},
  'movable-ink': {signalKey: 'movableInk', label: 'Movable Ink'},
  mparticle: {signalKey: 'mParticle', label: 'mParticle'},
  mysql: {signalKey: 'mySql', label: 'MySQL'},
  netlify: {signalKey: 'netlify', label: 'Netlify'},
  'new-relic': {manualOverride: true},
  nginx: {signalKey: 'nginx', label: 'NGINX'},
  noibu: {manualOverride: true},
  okta: {manualOverride: true},
  ometria: {signalKey: 'ometria', label: 'Ometria'},
  onesignal: {signalKey: 'oneSignal', label: 'OneSignal'},
  onetrust: {signalKey: 'oneTrust', label: 'OneTrust'},
  optimizely: {manualOverride: true},
  outbrain: {signalKey: 'outbrain', label: 'Outbrain'},
  pantheon: {signalKey: 'pantheon', label: 'Pantheon'},
  paypal: {signalKey: 'paypal', label: 'PayPal'},
  pendo: {signalKey: 'pendo', label: 'Pendo'},
  perimeterx: {signalKey: 'perimeterX', label: 'PerimeterX'},
  'pinterest-ads': {signalKey: 'pinterestAds', label: 'Pinterest Ads'},
  podium: {signalKey: 'podium', label: 'Podium'},
  posthog: {signalKey: 'posthog', label: 'Posthog'},
  powerreviews: {signalKey: 'powerReviews', label: 'PowerReviews'},
  qualified: {signalKey: 'qualified', label: 'Qualified'},
  qualtrics: {manualOverride: true},
  'quantcast-measure': {
    signalKey: 'quantcastMeasure',
    label: 'Quantcast Measure',
  },
  'quantum-metric': {manualOverride: true},
  'queue-it': {signalKey: 'queueIt', label: 'Queue-it'},
  'quora-pixel': {signalKey: 'quora', label: 'Quora Pixel'},
  react: {signalKey: 'react', label: 'React'},
  recaptcha: {signalKey: 'reCAPTCHA', label: 'reCAPTCHA'},
  recharge: {signalKey: 'recharge', label: 'Recharge Payments'},
  reddit: {signalKey: 'reddit', label: 'Reddit'},
  'reddit-ads': {signalKey: 'redditAds', label: 'Reddit Ads'},
  rewardful: {signalKey: 'rewardful', label: 'Rewardful'},
  rudderstack: {manualOverride: true},
  sailthru: {signalKey: 'sailthru', label: 'Sailthru'},
  salesforce: {manualOverride: true},
  'salesforce-marketing-cloud-account-engagement': {
    signalKey: 'pardot',
    label: 'Salesforce Marketing Cloud (Pardot)',
  },
  'salesforce-service-cloud': {manualOverride: true},
  salesloft: {signalKey: 'salesLoft', label: 'Salesloft'},
  segment: {signalKey: 'segment', label: 'Segment'},
  semrush: {signalKey: 'semrush', label: 'Semrush'},
  sendgrid: {signalKey: 'sendgrid', label: 'Sendgrid'},
  sentry: {signalKey: 'sentry', label: 'Sentry'},
  servicenow: {signalKey: 'serviceNow', label: 'ServiceNow'},
  sharpspring: {manualOverride: true},
  'sharpspring-ads': {manualOverride: true},
  shopify: {signalKey: 'shopify', label: 'Shopify'},
  'simpli-fi': {signalKey: 'simpliFi', label: 'Simpli.fi'},
  sitecore: {signalKey: 'sitecore', label: 'Sitecore'},
  siteimprove: {signalKey: 'siteimprove', label: 'Siteimprove'},
  'snowplow-analytics': {
    signalKey: 'snowplowAnalytics',
    label: 'Snowplow Analytics',
  },
  sparkpost: {signalKey: 'sparkpost', label: 'SparkPost'},
  sprig: {signalKey: 'sprig', label: 'Sprig'},
  squarespace: {signalKey: 'squarespace', label: 'Squarespace'},
  'squarespace-commerce': {
    signalKey: 'squarespaceCommerce',
    label: 'Squarespace Commerce',
  },
  stripe: {manualOverride: true},
  tapfiliate: {signalKey: 'tapfiliate', label: 'Tapfiliate'},
  tealium: {signalKey: 'tealium', label: 'Tealium'},
  thetradedesk: {signalKey: 'theTradeDesk', label: 'The Trade Desk'},
  'tiktok-pixel': {signalKey: 'tiktokPixel', label: 'TikTok Pixel'},
  totango: {signalKey: 'totango', label: 'Totango'},
  transcend: {signalKey: 'transcend', label: 'Transcend'},
  trustpilot: {signalKey: 'trustpilot', label: 'Trustpilot'},
  'twitter-ads': {signalKey: 'twitterAds', label: 'Twitter Ads'},
  typeform: {signalKey: 'typeform', label: 'Typeform'},
  unbounce: {signalKey: 'unbounce', label: 'Unbounce'},
  unpkg: {signalKey: 'unpkg', label: 'UNPKG'},
  userzoom: {manualOverride: true},
  vanilla: {manualOverride: true},
  vercel: {signalKey: 'vercel', label: 'Vercel'},
  vimeo: {signalKey: 'vimeo', label: 'Vimeo'},
  'vue-js': {signalKey: 'vue', label: 'Vue.js'},
  vwo: {signalKey: 'vwo', label: 'VWO Engage'},
  walkme: {signalKey: 'walkMe', label: 'WalkMe'},
  webengage: {signalKey: 'webEngage', label: 'WebEngage'},
  webflow: {manualOverride: true},
  'webflow-ecommerce': {manualOverride: true},
  whatfix: {signalKey: 'whatfix', label: 'Whatfix'},
  wistia: {signalKey: 'wistia', label: 'Wistia'},
  wix: {signalKey: 'wix', label: 'Wix'},
  woocommerce: {manualOverride: true},
  wordpress: {signalKey: 'wordpress', label: 'Wordpress'},
  'wp-engine': {signalKey: 'wpEngine', label: 'WP Engine'},
  'yahoo-advertising': {
    signalKey: 'yahooAdvertising',
    label: 'Yahoo Advertising',
  },
  'yotpo-reviews': {signalKey: 'yotpo', label: 'Yotpo Reviews'},
  youtube: {signalKey: 'youTube', label: 'YouTube'},
  zendesk: {manualOverride: true},
  'zoho-mail': {signalKey: 'zohoMail', label: 'Zoho Mail'},
  zoominfo: {signalKey: 'zoomInfo', label: 'ZoomInfo'},

  // --- auto-generated wapp technology signals ---
  '2b-advice': {signalKey: '2bAdvice', label: '2b Advice'},
  '4-tell': {signalKey: '4Tell', label: '4 Tell'},
  'a-frame': {signalKey: 'aFrame', label: 'A Frame'},
  'a3-lazy-load': {signalKey: 'a3LazyLoad', label: 'A3 Lazy Load'},
  'a8-net': {signalKey: 'a8Net', label: 'A8 Net'},
  'accentuate-custom-fields': {
    signalKey: 'accentuateCustomFields',
    label: 'Accentuate Custom Fields',
  },
  accessible360: {signalKey: 'accessible360', label: 'Accessible360'},
  accessibly: {signalKey: 'accessibly', label: 'Accessibly'},
  accesso: {signalKey: 'accesso', label: 'Accesso'},
  accuweather: {signalKey: 'accuweather', label: 'Accuweather'},
  ace: {signalKey: 'ace', label: 'Ace'},
  'acoustic-experience-analytics': {
    signalKey: 'acousticExperienceAnalytics',
    label: 'Acoustic Experience Analytics',
  },
  'acquia-campaign-factory': {
    signalKey: 'acquiaCampaignFactory',
    label: 'Acquia Campaign Factory',
  },
  'acquia-cloud-platform-cdn': {
    signalKey: 'acquiaCloudPlatformCdn',
    label: 'Acquia Cloud Platform Cdn',
  },
  'acquia-cloud-site-factory': {
    signalKey: 'acquiaCloudSiteFactory',
    label: 'Acquia Cloud Site Factory',
  },
  'acquia-content-hub': {
    signalKey: 'acquiaContentHub',
    label: 'Acquia Content Hub',
  },
  'acquia-customer-data-platform': {
    signalKey: 'acquiaCustomerDataPlatform',
    label: 'Acquia Customer Data Platform',
  },
  'acquia-personalization': {
    signalKey: 'acquiaPersonalization',
    label: 'Acquia Personalization',
  },
  'acquia-site-studio': {
    signalKey: 'acquiaSiteStudio',
    label: 'Acquia Site Studio',
  },
  'acquire-cobrowse': {signalKey: 'acquireCobrowse', label: 'Acquire Cobrowse'},
  'acquire-live-chat': {
    signalKey: 'acquireLiveChat',
    label: 'Acquire Live Chat',
  },
  'act-on': {signalKey: 'actOn', label: 'Act On'},
  actblue: {signalKey: 'actblue', label: 'Actblue'},
  actito: {signalKey: 'actito', label: 'Actito'},
  'acuity-scheduling': {
    signalKey: 'acuityScheduling',
    label: 'Acuity Scheduling',
  },
  acuityads: {signalKey: 'acuityads', label: 'Acuityads'},
  'ad-lightning': {signalKey: 'adLightning', label: 'Ad Lightning'},
  adalyser: {signalKey: 'adalyser', label: 'Adalyser'},
  adasitecompliance: {
    signalKey: 'adasitecompliance',
    label: 'Adasitecompliance',
  },
  addevent: {signalKey: 'addevent', label: 'Addevent'},
  addshoppers: {signalKey: 'addshoppers', label: 'Addshoppers'},
  addthis: {signalKey: 'addthis', label: 'Addthis'},
  addtoany: {signalKey: 'addtoany', label: 'Addtoany'},
  'addtoany-share-buttons': {
    signalKey: 'addtoanyShareButtons',
    label: 'Addtoany Share Buttons',
  },
  adform: {signalKey: 'adform', label: 'Adform'},
  adjust: {signalKey: 'adjust', label: 'Adjust'},
  admiral: {signalKey: 'admiral', label: 'Admiral'},
  admitad: {signalKey: 'admitad', label: 'Admitad'},
  'admo-tv': {signalKey: 'admoTv', label: 'Admo Tv'},
  'adobe-client-data-layer': {
    signalKey: 'adobeClientDataLayer',
    label: 'Adobe Client Data Layer',
  },
  'adobe-coldfusion': {signalKey: 'adobeColdfusion', label: 'Adobe Coldfusion'},
  'adobe-dtm': {signalKey: 'adobeDtm', label: 'Adobe Dtm'},
  'adobe-dynamic-media-classic': {
    signalKey: 'adobeDynamicMediaClassic',
    label: 'Adobe Dynamic Media Classic',
  },
  'adobe-experience-manager-franklin': {
    signalKey: 'adobeExperienceManagerFranklin',
    label: 'Adobe Experience Manager Franklin',
  },
  'adobe-experience-platform-identity-service': {
    signalKey: 'adobeExperiencePlatformIdentityService',
    label: 'Adobe Experience Platform Identity Service',
  },
  'adobe-experience-platform-launch': {
    signalKey: 'adobeExperiencePlatformLaunch',
    label: 'Adobe Experience Platform Launch',
  },
  'adobe-flash': {signalKey: 'adobeFlash', label: 'Adobe Flash'},
  'adobe-golive': {signalKey: 'adobeGolive', label: 'Adobe Golive'},
  'adobe-robohelp': {signalKey: 'adobeRobohelp', label: 'Adobe Robohelp'},
  adopt: {signalKey: 'adopt', label: 'Adopt'},
  'adroll-cmp-system': {
    signalKey: 'adrollCmpSystem',
    label: 'Adroll Cmp System',
  },
  adthrive: {signalKey: 'adthrive', label: 'Adthrive'},
  'advanced-custom-fields': {
    signalKey: 'advancedCustomFields',
    label: 'Advanced Custom Fields',
  },
  'affiliate-b': {signalKey: 'affiliateB', label: 'Affiliate B'},
  'affiliate-future': {signalKey: 'affiliateFuture', label: 'Affiliate Future'},
  affiliatly: {signalKey: 'affiliatly', label: 'Affiliatly'},
  affirm: {signalKey: 'affirm', label: 'Affirm'},
  afterpay: {signalKey: 'afterpay', label: 'Afterpay'},
  'aftership-returns-center': {
    signalKey: 'aftershipReturnsCenter',
    label: 'Aftership Returns Center',
  },
  ahoy: {signalKey: 'ahoy', label: 'Ahoy'},
  aimtell: {signalKey: 'aimtell', label: 'Aimtell'},
  air360: {signalKey: 'air360', label: 'Air360'},
  aircall: {signalKey: 'aircall', label: 'Aircall'},
  airship: {signalKey: 'airship', label: 'Airship'},
  airtable: {signalKey: 'airtable', label: 'Airtable'},
  'akamai-bot-manager': {
    signalKey: 'akamaiBotManager',
    label: 'Akamai Bot Manager',
  },
  'akamai-mpulse': {signalKey: 'akamaiMpulse', label: 'Akamai Mpulse'},
  'akamai-web-application-protector': {
    signalKey: 'akamaiWebApplicationProtector',
    label: 'Akamai Web Application Protector',
  },
  akismet: {signalKey: 'akismet', label: 'Akismet'},
  aklamio: {signalKey: 'aklamio', label: 'Aklamio'},
  albacross: {signalKey: 'albacross', label: 'Albacross'},
  alertifyjs: {signalKey: 'alertifyjs', label: 'Alertifyjs'},
  'alexa-certified-site-metrics': {
    signalKey: 'alexaCertifiedSiteMetrics',
    label: 'Alexa Certified Site Metrics',
  },
  'algolia-docsearch': {
    signalKey: 'algoliaDocsearch',
    label: 'Algolia Docsearch',
  },
  'alibaba-cloud-cdn': {
    signalKey: 'alibabaCloudCdn',
    label: 'Alibaba Cloud Cdn',
  },
  'all-in-one-seo-pack': {
    signalKey: 'allInOneSeoPack',
    label: 'All In One Seo Pack',
  },
  'all-inkl': {signalKey: 'allInkl', label: 'All Inkl'},
  alli: {signalKey: 'alli', label: 'Alli'},
  alloyui: {signalKey: 'alloyui', label: 'Alloyui'},
  allyable: {signalKey: 'allyable', label: 'Allyable'},
  almalinux: {signalKey: 'almalinux', label: 'Almalinux'},
  'alpine-js': {signalKey: 'alpineJs', label: 'Alpine Js'},
  amaya: {signalKey: 'amaya', label: 'Amaya'},
  'amazon-alb': {signalKey: 'amazonAlb', label: 'Amazon Alb'},
  'amazon-associates': {
    signalKey: 'amazonAssociates',
    label: 'Amazon Associates',
  },
  'amazon-cloudwatch-rum': {
    signalKey: 'amazonCloudwatchRum',
    label: 'Amazon Cloudwatch Rum',
  },
  'amazon-ec2': {signalKey: 'amazonEc2', label: 'Amazon Ec2'},
  'amazon-ecs': {signalKey: 'amazonEcs', label: 'Amazon Ecs'},
  'amazon-elb': {signalKey: 'amazonElb', label: 'Amazon Elb'},
  'amazon-webstore': {signalKey: 'amazonWebstore', label: 'Amazon Webstore'},
  amcharts: {signalKey: 'amcharts', label: 'Amcharts'},
  'american-express': {signalKey: 'americanExpress', label: 'American Express'},
  'amex-express-checkout': {
    signalKey: 'amexExpressCheckout',
    label: 'Amex Express Checkout',
  },
  amobee: {signalKey: 'amobee', label: 'Amobee'},
  amp: {signalKey: 'amp', label: 'Amp'},
  'amp-for-wordpress': {
    signalKey: 'ampForWordpress',
    label: 'Amp For Wordpress',
  },
  amplience: {signalKey: 'amplience', label: 'Amplience'},
  'angular-material': {signalKey: 'angularMaterial', label: 'Angular Material'},
  angularjs: {signalKey: 'angularjs', label: 'Angularjs'},
  'animate-css': {signalKey: 'animateCss', label: 'Animate Css'},
  'anime-js': {signalKey: 'animeJs', label: 'Anime Js'},
  'aniview-ad-server': {
    signalKey: 'aniviewAdServer',
    label: 'Aniview Ad Server',
  },
  'aniview-video-ad-player': {
    signalKey: 'aniviewVideoAdPlayer',
    label: 'Aniview Video Ad Player',
  },
  'ant-design': {signalKey: 'antDesign', label: 'Ant Design'},
  'anthology-encompass': {
    signalKey: 'anthologyEncompass',
    label: 'Anthology Encompass',
  },
  aos: {signalKey: 'aos', label: 'Aos'},
  'apache-apisix': {signalKey: 'apacheApisix', label: 'Apache Apisix'},
  'apache-http-server': {
    signalKey: 'apacheHttpServer',
    label: 'Apache Http Server',
  },
  'apache-tomcat': {signalKey: 'apacheTomcat', label: 'Apache Tomcat'},
  'apache-traffic-server': {
    signalKey: 'apacheTrafficServer',
    label: 'Apache Traffic Server',
  },
  'apache-wicket': {signalKey: 'apacheWicket', label: 'Apache Wicket'},
  apc: {signalKey: 'apc', label: 'Apc'},
  'apexcharts-js': {signalKey: 'apexchartsJs', label: 'Apexcharts Js'},
  apexchat: {signalKey: 'apexchat', label: 'Apexchat'},
  apexpages: {signalKey: 'apexpages', label: 'Apexpages'},
  aplazame: {signalKey: 'aplazame', label: 'Aplazame'},
  'apollo13themes-rife': {
    signalKey: 'apollo13themesRife',
    label: 'Apollo13themes Rife',
  },
  apostrophecms: {signalKey: 'apostrophecms', label: 'Apostrophecms'},
  appdynamics: {signalKey: 'appdynamics', label: 'Appdynamics'},
  'apple-business-chat': {
    signalKey: 'appleBusinessChat',
    label: 'Apple Business Chat',
  },
  'apple-icloud-mail': {
    signalKey: 'appleIcloudMail',
    label: 'Apple Icloud Mail',
  },
  'apple-mapkit-js': {signalKey: 'appleMapkitJs', label: 'Apple Mapkit Js'},
  'apple-pay': {signalKey: 'applePay', label: 'Apple Pay'},
  'apple-sign-in': {signalKey: 'appleSignIn', label: 'Apple Sign In'},
  applicantstack: {signalKey: 'applicantstack', label: 'Applicantstack'},
  'application-request-routing': {
    signalKey: 'applicationRequestRouting',
    label: 'Application Request Routing',
  },
  appnexus: {signalKey: 'appnexus', label: 'Appnexus'},
  appsflyer: {signalKey: 'appsflyer', label: 'Appsflyer'},
  aprimo: {signalKey: 'aprimo', label: 'Aprimo'},
  'arc-xp': {signalKey: 'arcXp', label: 'Arc Xp'},
  'arcgis-api-for-javascript': {
    signalKey: 'arcgisApiForJavascript',
    label: 'Arcgis Api For Javascript',
  },
  'aruba-it': {signalKey: 'arubaIt', label: 'Aruba It'},
  arvancloud: {signalKey: 'arvancloud', label: 'Arvancloud'},
  asciinema: {signalKey: 'asciinema', label: 'Asciinema'},
  asendia: {signalKey: 'asendia', label: 'Asendia'},
  'asgaros-forum': {signalKey: 'asgarosForum', label: 'Asgaros Forum'},
  'asp-net-boilerplate': {
    signalKey: 'aspNetBoilerplate',
    label: 'Asp Net Boilerplate',
  },
  astra: {signalKey: 'astra', label: 'Astra'},
  'astra-widgets': {signalKey: 'astraWidgets', label: 'Astra Widgets'},
  astro: {signalKey: 'astro', label: 'Astro'},
  'astute-solutions': {signalKey: 'astuteSolutions', label: 'Astute Solutions'},
  'at-internet-analyzer': {
    signalKey: 'atInternetAnalyzer',
    label: 'At Internet Analyzer',
  },
  'at-internet-xiti': {signalKey: 'atInternetXiti', label: 'At Internet Xiti'},
  atatus: {signalKey: 'atatus', label: 'Atatus'},
  'athemes-astrid': {signalKey: 'athemesAstrid', label: 'Athemes Astrid'},
  'athemes-moesia': {signalKey: 'athemesMoesia', label: 'Athemes Moesia'},
  'athemes-sydney': {signalKey: 'athemesSydney', label: 'Athemes Sydney'},
  'atlassian-jira-issue-collector': {
    signalKey: 'atlassianJiraIssueCollector',
    label: 'Atlassian Jira Issue Collector',
  },
  'atlassian-statuspage': {
    signalKey: 'atlassianStatuspage',
    label: 'Atlassian Statuspage',
  },
  audioeye: {signalKey: 'audioeye', label: 'Audioeye'},
  aurelia: {signalKey: 'aurelia', label: 'Aurelia'},
  auryc: {signalKey: 'auryc', label: 'Auryc'},
  'australia-post': {signalKey: 'australiaPost', label: 'Australia Post'},
  auth0: {signalKey: 'wappAuth0', label: 'Auth0 (Web)'},
  'auth0-lock': {signalKey: 'auth0Lock', label: 'Auth0 Lock'},
  'authorize-net': {signalKey: 'authorizeNet', label: 'Authorize Net'},
  'autocomplete-js': {signalKey: 'autocompleteJs', label: 'Autocomplete Js'},
  'automatic-css': {signalKey: 'automaticCss', label: 'Automatic Css'},
  automizely: {signalKey: 'automizely', label: 'Automizely'},
  autoptimize: {signalKey: 'autoptimize', label: 'Autoptimize'},
  'avada-seo': {signalKey: 'avadaSeo', label: 'Avada Seo'},
  'avis-verifies': {signalKey: 'avisVerifies', label: 'Avis Verifies'},
  aweber: {signalKey: 'aweber', label: 'Aweber'},
  awesomplete: {signalKey: 'awesomplete', label: 'Awesomplete'},
  'aws-certificate-manager': {
    signalKey: 'awsCertificateManager',
    label: 'Aws Certificate Manager',
  },
  'aws-waf-captcha': {signalKey: 'awsWafCaptcha', label: 'Aws Waf Captcha'},
  axeptio: {signalKey: 'axeptio', label: 'Axeptio'},
  axios: {signalKey: 'axios', label: 'Axios'},
  'azure-cdn': {signalKey: 'azureCdn', label: 'Azure Cdn'},
  'azure-edge-network': {
    signalKey: 'azureEdgeNetwork',
    label: 'Azure Edge Network',
  },
  'azure-front-door': {signalKey: 'azureFrontDoor', label: 'Azure Front Door'},
  'azure-monitor': {signalKey: 'azureMonitor', label: 'Azure Monitor'},
  babel: {signalKey: 'babel', label: 'Babel'},
  bablic: {signalKey: 'bablic', label: 'Bablic'},
  babylist: {signalKey: 'babylist', label: 'Babylist'},
  'babylon-js': {signalKey: 'babylonJs', label: 'Babylon Js'},
  'back-in-stock': {signalKey: 'backInStock', label: 'Back In Stock'},
  'backbone-js': {signalKey: 'backboneJs', label: 'Backbone Js'},
  backdrop: {signalKey: 'backdrop', label: 'Backdrop'},
  'baidu-analytics': {signalKey: 'baiduAnalytics', label: 'Baidu Analytics'},
  'baidu-maps': {signalKey: 'baiduMaps', label: 'Baidu Maps'},
  bamboohr: {manualOverride: true},
  bambuser: {signalKey: 'bambuser', label: 'Bambuser'},
  'barba-js': {signalKey: 'barbaJs', label: 'Barba Js'},
  basic: {signalKey: 'basic', label: 'Basic'},
  'bazaarvoice-curation': {
    signalKey: 'bazaarvoiceCuration',
    label: 'Bazaarvoice Curation',
  },
  'beam-aftersell': {signalKey: 'beamAftersell', label: 'Beam Aftersell'},
  beamer: {signalKey: 'beamer', label: 'Beamer'},
  'beehiiv-rss-feed': {signalKey: 'beehiivRssFeed', label: 'Beehiiv Rss Feed'},
  beeketing: {signalKey: 'beeketing', label: 'Beeketing'},
  bentobox: {signalKey: 'bentobox', label: 'Bentobox'},
  'better-stack': {signalKey: 'betterStack', label: 'Better Stack'},
  betterdocs: {signalKey: 'betterdocs', label: 'Betterdocs'},
  'betterdocs-plugin': {
    signalKey: 'betterdocsPlugin',
    label: 'Betterdocs Plugin',
  },
  beyable: {signalKey: 'beyable', label: 'Beyable'},
  birdeye: {signalKey: 'birdeye', label: 'Birdeye'},
  bitrix24: {signalKey: 'bitrix24', label: 'Bitrix24'},
  blazor: {signalKey: 'blazor', label: 'Blazor'},
  blitz: {signalKey: 'blitz', label: 'Blitz'},
  blocksy: {signalKey: 'blocksy', label: 'Blocksy'},
  'blocksy-companion': {
    signalKey: 'blocksyCompanion',
    label: 'Blocksy Companion',
  },
  blogger: {signalKey: 'blogger', label: 'Blogger'},
  'bloomreach-discovery': {
    signalKey: 'bloomreachDiscovery',
    label: 'Bloomreach Discovery',
  },
  blue: {signalKey: 'blue', label: 'Blue'},
  'blue-triangle': {signalKey: 'blueTriangle', label: 'Blue Triangle'},
  bluehost: {signalKey: 'bluehost', label: 'Bluehost'},
  bokeh: {signalKey: 'bokeh', label: 'Bokeh'},
  'bold-bundles': {signalKey: 'boldBundles', label: 'Bold Bundles'},
  'bold-chat': {signalKey: 'boldChat', label: 'Bold Chat'},
  'bold-commerce': {signalKey: 'boldCommerce', label: 'Bold Commerce'},
  'bold-custom-pricing': {
    signalKey: 'boldCustomPricing',
    label: 'Bold Custom Pricing',
  },
  'bold-page-builder': {
    signalKey: 'boldPageBuilder',
    label: 'Bold Page Builder',
  },
  'bold-product-options': {
    signalKey: 'boldProductOptions',
    label: 'Bold Product Options',
  },
  'bold-subscriptions': {
    signalKey: 'boldSubscriptions',
    label: 'Bold Subscriptions',
  },
  'bold-themes': {signalKey: 'boldThemes', label: 'Bold Themes'},
  'bold-upsell': {signalKey: 'boldUpsell', label: 'Bold Upsell'},
  boldgrid: {signalKey: 'boldgrid', label: 'Boldgrid'},
  'bolt-cms': {signalKey: 'boltCms', label: 'Bolt Cms'},
  'bolt-payments': {signalKey: 'boltPayments', label: 'Bolt Payments'},
  'booking-com': {signalKey: 'bookingCom', label: 'Booking Com'},
  'booking-com-widget': {
    signalKey: 'bookingComWidget',
    label: 'Booking Com Widget',
  },
  bookly: {signalKey: 'bookly', label: 'Bookly'},
  boomerang: {signalKey: 'boomerang', label: 'Boomerang'},
  'boost-commerce': {signalKey: 'boostCommerce', label: 'Boost Commerce'},
  'booster-page-speed-optimizer': {
    signalKey: 'boosterPageSpeedOptimizer',
    label: 'Booster Page Speed Optimizer',
  },
  bootstrap: {signalKey: 'bootstrap', label: 'Bootstrap'},
  'bootstrap-icons': {signalKey: 'bootstrapIcons', label: 'Bootstrap Icons'},
  'bootstrap-table': {signalKey: 'bootstrapTable', label: 'Bootstrap Table'},
  borderfree: {signalKey: 'borderfree', label: 'Borderfree'},
  'borlabs-cookie': {signalKey: 'borlabsCookie', label: 'Borlabs Cookie'},
  braintree: {signalKey: 'braintree', label: 'Braintree'},
  branch: {signalKey: 'branch', label: 'Branch'},
  brandfolder: {signalKey: 'brandfolder', label: 'Brandfolder'},
  bread: {signalKey: 'bread', label: 'Bread'},
  'breadcrumb-navxt': {signalKey: 'breadcrumbNavxt', label: 'Breadcrumb Navxt'},
  breakdance: {signalKey: 'breakdance', label: 'Breakdance'},
  breinify: {signalKey: 'breinify', label: 'Breinify'},
  bricks: {signalKey: 'bricks', label: 'Bricks'},
  brightinfo: {signalKey: 'brightinfo', label: 'Brightinfo'},
  brightspot: {signalKey: 'brightspot', label: 'Brightspot'},
  'brilliant-web-to-lead': {
    signalKey: 'brilliantWebToLead',
    label: 'Brilliant Web To Lead',
  },
  broadstreet: {signalKey: 'broadstreet', label: 'Broadstreet'},
  'browser-update-org': {
    signalKey: 'browserUpdateOrg',
    label: 'Browser Update Org',
  },
  brt: {signalKey: 'brt', label: 'Brt'},
  bubble: {signalKey: 'bubble', label: 'Bubble'},
  buddypress: {signalKey: 'buddypress', label: 'Buddypress'},
  bugcrowd: {signalKey: 'bugcrowd', label: 'Bugcrowd'},
  bugherd: {signalKey: 'bugherd', label: 'Bugherd'},
  bugsnag: {signalKey: 'bugsnag', label: 'Bugsnag'},
  'builder-io': {signalKey: 'builderIo', label: 'Builder Io'},
  bulma: {signalKey: 'bulma', label: 'Bulma'},
  bunny: {signalKey: 'bunny', label: 'Bunny'},
  'bunny-fonts': {signalKey: 'bunnyFonts', label: 'Bunny Fonts'},
  'business-catalyst': {
    signalKey: 'businessCatalyst',
    label: 'Business Catalyst',
  },
  buttercms: {signalKey: 'buttercms', label: 'Buttercms'},
  buttonizer: {signalKey: 'buttonizer', label: 'Buttonizer'},
  'buy-me-a-coffee': {signalKey: 'buyMeACoffee', label: 'Buy Me A Coffee'},
  buysellads: {signalKey: 'buysellads', label: 'Buysellads'},
  caddy: {signalKey: 'caddy', label: 'Caddy'},
  cakephp: {signalKey: 'cakephp', label: 'Cakephp'},
  'caldera-forms': {signalKey: 'calderaForms', label: 'Caldera Forms'},
  calendly: {signalKey: 'calendly', label: 'Calendly'},
  calltrackingmetrics: {
    signalKey: 'calltrackingmetrics',
    label: 'Calltrackingmetrics',
  },
  canny: {signalKey: 'canny', label: 'Canny'},
  canto: {signalKey: 'canto', label: 'Canto'},
  canvasjs: {signalKey: 'canvasjs', label: 'Canvasjs'},
  'captivate-fm': {signalKey: 'captivateFm', label: 'Captivate Fm'},
  'carbon-ads': {signalKey: 'carbonAds', label: 'Carbon Ads'},
  carecart: {signalKey: 'carecart', label: 'Carecart'},
  'carecart-sales-pop-up': {
    signalKey: 'carecartSalesPopUp',
    label: 'Carecart Sales Pop Up',
  },
  cargo: {signalKey: 'cargo', label: 'Cargo'},
  carro: {signalKey: 'carro', label: 'Carro'},
  'cart-com': {signalKey: 'cartCom', label: 'Cart Com'},
  'cart-js': {signalKey: 'cartJs', label: 'Cart Js'},
  cartstack: {signalKey: 'cartstack', label: 'Cartstack'},
  cdnjs: {signalKey: 'cdnjs', label: 'Cdnjs'},
  centminmod: {signalKey: 'centminmod', label: 'Centminmod'},
  centos: {signalKey: 'centos', label: 'Centos'},
  cfml: {signalKey: 'cfml', label: 'Cfml'},
  'chakra-ui': {signalKey: 'chakraUi', label: 'Chakra Ui'},
  chameleon: {signalKey: 'chameleon', label: 'Chameleon'},
  'channel-io': {signalKey: 'channelIo', label: 'Channel Io'},
  channeladvisor: {signalKey: 'channeladvisor', label: 'Channeladvisor'},
  chaport: {signalKey: 'chaport', label: 'Chaport'},
  'chart-js': {signalKey: 'chartJs', label: 'Chart Js'},
  chartbeat: {signalKey: 'chartbeat', label: 'Chartbeat'},
  chatra: {signalKey: 'chatra', label: 'Chatra'},
  chatstack: {signalKey: 'chatstack', label: 'Chatstack'},
  chatwoot: {signalKey: 'chatwoot', label: 'Chatwoot'},
  chekkit: {signalKey: 'chekkit', label: 'Chekkit'},
  chimpmatic: {signalKey: 'chimpmatic', label: 'Chimpmatic'},
  choices: {signalKey: 'choices', label: 'Choices'},
  chronopost: {signalKey: 'chronopost', label: 'Chronopost'},
  civic: {signalKey: 'civic', label: 'Civic'},
  civictheme: {signalKey: 'civictheme', label: 'Civictheme'},
  ckeditor: {signalKey: 'ckeditor', label: 'Ckeditor'},
  clarip: {signalKey: 'clarip', label: 'Clarip'},
  clarity: {signalKey: 'clarity', label: 'Clarity'},
  classy: {signalKey: 'classy', label: 'Classy'},
  clearsale: {signalKey: 'clearsale', label: 'Clearsale'},
  clerk: {signalKey: 'clerk', label: 'Clerk'},
  'click-pledge': {signalKey: 'clickPledge', label: 'Click Pledge'},
  clickbank: {signalKey: 'clickbank', label: 'Clickbank'},
  clickcease: {signalKey: 'clickcease', label: 'Clickcease'},
  clickdimensions: {signalKey: 'clickdimensions', label: 'Clickdimensions'},
  clickfunnels: {signalKey: 'clickfunnels', label: 'Clickfunnels'},
  clickheat: {signalKey: 'clickheat', label: 'Clickheat'},
  clicktale: {signalKey: 'clicktale', label: 'Clicktale'},
  clientjs: {signalKey: 'clientjs', label: 'Clientjs'},
  clinch: {signalKey: 'clinch', label: 'Clinch'},
  'clipboard-js': {signalKey: 'clipboardJs', label: 'Clipboard Js'},
  'closure-library': {signalKey: 'closureLibrary', label: 'Closure Library'},
  cloudbeds: {signalKey: 'cloudbeds', label: 'Cloudbeds'},
  cloudcannon: {signalKey: 'cloudcannon', label: 'Cloudcannon'},
  'cloudflare-bot-management': {
    signalKey: 'cloudflareBotManagement',
    label: 'Cloudflare Bot Management',
  },
  'cloudflare-browser-insights': {
    signalKey: 'cloudflareBrowserInsights',
    label: 'Cloudflare Browser Insights',
  },
  'cloudflare-rocket-loader': {
    signalKey: 'cloudflareRocketLoader',
    label: 'Cloudflare Rocket Loader',
  },
  'cloudflare-stream': {
    signalKey: 'cloudflareStream',
    label: 'Cloudflare Stream',
  },
  'cloudflare-turnstile': {
    signalKey: 'cloudflareTurnstile',
    label: 'Cloudflare Turnstile',
  },
  'cloudflare-workers': {
    signalKey: 'cloudflareWorkers',
    label: 'Cloudflare Workers',
  },
  'cloudflare-zaraz': {signalKey: 'cloudflareZaraz', label: 'Cloudflare Zaraz'},
  cloudimage: {signalKey: 'cloudimage', label: 'Cloudimage'},
  cloudways: {signalKey: 'cloudways', label: 'Cloudways'},
  cluep: {signalKey: 'cluep', label: 'Cluep'},
  clutch: {signalKey: 'clutch', label: 'Clutch'},
  'cms-made-simple': {signalKey: 'cmsMadeSimple', label: 'Cms Made Simple'},
  cnzz: {signalKey: 'cnzz', label: 'Cnzz'},
  codeigniter: {signalKey: 'codeigniter', label: 'Codeigniter'},
  codemirror: {signalKey: 'codemirror', label: 'Codemirror'},
  codesandbox: {signalKey: 'codesandbox', label: 'Codesandbox'},
  'coinbase-commerce': {
    signalKey: 'coinbaseCommerce',
    label: 'Coinbase Commerce',
  },
  'colibri-wp': {signalKey: 'colibriWp', label: 'Colibri Wp'},
  colissimo: {signalKey: 'colissimo', label: 'Colissimo'},
  'colorlib-shapely': {signalKey: 'colorlibShapely', label: 'Colorlib Shapely'},
  comm100: {signalKey: 'comm100', label: 'Comm100'},
  'commanders-act-tagcommander': {
    signalKey: 'commandersActTagcommander',
    label: 'Commanders Act Tagcommander',
  },
  'commanders-act-trustcommander': {
    signalKey: 'commandersActTrustcommander',
    label: 'Commanders Act Trustcommander',
  },
  commerce7: {signalKey: 'commerce7', label: 'Commerce7'},
  complianz: {signalKey: 'complianz', label: 'Complianz'},
  'concrete-cms': {signalKey: 'concreteCms', label: 'Concrete Cms'},
  confiant: {signalKey: 'confiant', label: 'Confiant'},
  connectif: {signalKey: 'connectif', label: 'Connectif'},
  'contact-form-7': {signalKey: 'contactForm7', label: 'Contact Form 7'},
  contao: {signalKey: 'contao', label: 'Contao'},
  contentstack: {signalKey: 'contentstack', label: 'Contentstack'},
  contentstudio: {signalKey: 'contentstudio', label: 'Contentstudio'},
  contlo: {signalKey: 'contlo', label: 'Contlo'},
  'conversant-consent-tool': {
    signalKey: 'conversantConsentTool',
    label: 'Conversant Consent Tool',
  },
  convert: {signalKey: 'convert', label: 'Convert'},
  convertcart: {signalKey: 'convertcart', label: 'Convertcart'},
  convertflow: {signalKey: 'convertflow', label: 'Convertflow'},
  convertkit: {signalKey: 'convertkit', label: 'Convertkit'},
  conveythis: {signalKey: 'conveythis', label: 'Conveythis'},
  conviva: {signalKey: 'conviva', label: 'Conviva'},
  'cookie-information': {
    signalKey: 'cookieInformation',
    label: 'Cookie Information',
  },
  'cookie-information-plugin': {
    signalKey: 'cookieInformationPlugin',
    label: 'Cookie Information Plugin',
  },
  'cookie-notice': {signalKey: 'cookieNotice', label: 'Cookie Notice'},
  'cookie-script': {signalKey: 'cookieScript', label: 'Cookie Script'},
  cookiebot: {signalKey: 'cookiebot', label: 'Cookiebot'},
  cookiefirst: {signalKey: 'cookiefirst', label: 'Cookiefirst'},
  cookiehub: {signalKey: 'cookiehub', label: 'Cookiehub'},
  cookieyes: {signalKey: 'cookieyes', label: 'Cookieyes'},
  cooladata: {signalKey: 'cooladata', label: 'Cooladata'},
  copypoison: {signalKey: 'copypoison', label: 'Copypoison'},
  'core-js': {signalKey: 'coreJs', label: 'Core Js'},
  'coremedia-content-cloud': {
    signalKey: 'coremediaContentCloud',
    label: 'Coremedia Content Cloud',
  },
  correos: {signalKey: 'correos', label: 'Correos'},
  cosmic: {signalKey: 'cosmic', label: 'Cosmic'},
  countly: {signalKey: 'countly', label: 'Countly'},
  'covet-pics': {signalKey: 'covetPics', label: 'Covet Pics'},
  cowboy: {signalKey: 'cowboy', label: 'Cowboy'},
  'cozy-antitheft': {signalKey: 'cozyAntitheft', label: 'Cozy Antitheft'},
  cpanel: {signalKey: 'cpanel', label: 'Cpanel'},
  'craft-cms': {signalKey: 'craftCms', label: 'Craft Cms'},
  'craft-commerce': {signalKey: 'craftCommerce', label: 'Craft Commerce'},
  cratejoy: {signalKey: 'cratejoy', label: 'Cratejoy'},
  createjs: {signalKey: 'createjs', label: 'Createjs'},
  'creativ-email': {signalKey: 'creativEmail', label: 'Creativ Email'},
  crikle: {signalKey: 'crikle', label: 'Crikle'},
  crobox: {signalKey: 'crobox', label: 'Crobox'},
  'crocoblock-jetelements': {
    signalKey: 'crocoblockJetelements',
    label: 'Crocoblock Jetelements',
  },
  'cross-pixel': {signalKey: 'crossPixel', label: 'Cross Pixel'},
  'cross-sell': {signalKey: 'crossSell', label: 'Cross Sell'},
  crownpeak: {signalKey: 'crownpeak', label: 'Crownpeak'},
  'cryout-creations-bravada': {
    signalKey: 'cryoutCreationsBravada',
    label: 'Cryout Creations Bravada',
  },
  'crypto-js': {signalKey: 'cryptoJs', label: 'Crypto Js'},
  'cs-cart': {signalKey: 'csCart', label: 'Cs Cart'},
  ctt: {signalKey: 'ctt', label: 'Ctt'},
  cufon: {signalKey: 'cufon', label: 'Cufon'},
  'custom-fonts': {signalKey: 'customFonts', label: 'Custom Fonts'},
  customily: {signalKey: 'customily', label: 'Customily'},
  cwicly: {signalKey: 'cwicly', label: 'Cwicly'},
  cxense: {signalKey: 'cxense', label: 'Cxense'},
  'cyberchimps-responsive': {
    signalKey: 'cyberchimpsResponsive',
    label: 'Cyberchimps Responsive',
  },
  cybersource: {signalKey: 'cybersource', label: 'Cybersource'},
  d3: {signalKey: 'd3', label: 'D3'},
  dachser: {signalKey: 'dachser', label: 'Dachser'},
  dailykarma: {signalKey: 'dailykarma', label: 'Dailykarma'},
  'darkmode-js': {signalKey: 'darkmodeJs', label: 'Darkmode Js'},
  dart: {signalKey: 'dart', label: 'Dart'},
  datadog: {signalKey: 'wappDatadog', label: 'Datadog (Web)'},
  datatables: {signalKey: 'datatables', label: 'Datatables'},
  datatrics: {signalKey: 'datatrics', label: 'Datatrics'},
  datocms: {signalKey: 'datocms', label: 'Datocms'},
  'day-js': {signalKey: 'dayJs', label: 'Day Js'},
  'db-ip': {signalKey: 'dbIp', label: 'Db Ip'},
  'ddos-guard': {signalKey: 'ddosGuard', label: 'Ddos Guard'},
  'dealer-spike': {signalKey: 'dealerSpike', label: 'Dealer Spike'},
  debian: {signalKey: 'debian', label: 'Debian'},
  decibel: {signalKey: 'decibel', label: 'Decibel'},
  'decimal-js': {signalKey: 'decimalJs', label: 'Decimal Js'},
  delacon: {signalKey: 'delacon', label: 'Delacon'},
  deliverr: {signalKey: 'deliverr', label: 'Deliverr'},
  deno: {signalKey: 'deno', label: 'Deno'},
  'deno-deploy': {signalKey: 'denoDeploy', label: 'Deno Deploy'},
  'derak-cloud': {signalKey: 'derakCloud', label: 'Derak Cloud'},
  detectify: {signalKey: 'detectify', label: 'Detectify'},
  'deutsche-post': {signalKey: 'deutschePost', label: 'Deutsche Post'},
  dhl: {signalKey: 'dhl', label: 'Dhl'},
  dianomi: {signalKey: 'dianomi', label: 'Dianomi'},
  didomi: {signalKey: 'didomi', label: 'Didomi'},
  digicert: {signalKey: 'digicert', label: 'Digicert'},
  'digitalocean-spaces': {
    signalKey: 'digitaloceanSpaces',
    label: 'Digitalocean Spaces',
  },
  digitalriver: {signalKey: 'digitalriver', label: 'Digitalriver'},
  disqus: {signalKey: 'disqus', label: 'Disqus'},
  divi: {signalKey: 'divi', label: 'Divi'},
  django: {signalKey: 'django', label: 'Django'},
  dnn: {signalKey: 'dnn', label: 'Dnn'},
  docker: {signalKey: 'wappDocker', label: 'Docker (Web)'},
  docusaurus: {signalKey: 'docusaurus', label: 'Docusaurus'},
  dojo: {signalKey: 'dojo', label: 'Dojo'},
  dokan: {signalKey: 'dokan', label: 'Dokan'},
  domainfactory: {signalKey: 'domainfactory', label: 'Domainfactory'},
  donorbox: {signalKey: 'donorbox', label: 'Donorbox'},
  donorperfect: {signalKey: 'donorperfect', label: 'Donorperfect'},
  doofinder: {signalKey: 'doofinder', label: 'Doofinder'},
  dotdigital: {signalKey: 'dotdigital', label: 'Dotdigital'},
  'doubleclick-ad-exchange-adx': {
    signalKey: 'doubleclickAdExchangeAdx',
    label: 'Doubleclick Ad Exchange Adx',
  },
  'doubleclick-campaign-manager-dcm': {
    signalKey: 'doubleclickCampaignManagerDcm',
    label: 'Doubleclick Campaign Manager Dcm',
  },
  'doubleclick-floodlight': {
    signalKey: 'doubleclickFloodlight',
    label: 'Doubleclick Floodlight',
  },
  'doubleclick-for-publishers-dfp': {
    signalKey: 'doubleclickForPublishersDfp',
    label: 'Doubleclick For Publishers Dfp',
  },
  doubleverify: {signalKey: 'doubleverify', label: 'Doubleverify'},
  'download-monitor': {signalKey: 'downloadMonitor', label: 'Download Monitor'},
  dpd: {signalKey: 'dpd', label: 'Dpd'},
  'draftpress-hfcm': {signalKey: 'draftpressHfcm', label: 'Draftpress Hfcm'},
  drapr: {signalKey: 'drapr', label: 'Drapr'},
  dreamdata: {signalKey: 'dreamdata', label: 'Dreamdata'},
  dreamweaver: {signalKey: 'dreamweaver', label: 'Dreamweaver'},
  dropbox: {signalKey: 'dropbox', label: 'Dropbox'},
  dropinblog: {signalKey: 'dropinblog', label: 'Dropinblog'},
  dropzone: {signalKey: 'dropzone', label: 'Dropzone'},
  'drupal-commerce': {signalKey: 'drupalCommerce', label: 'Drupal Commerce'},
  'drupal-multisite': {signalKey: 'drupalMultisite', label: 'Drupal Multisite'},
  dtscout: {signalKey: 'dtscout', label: 'Dtscout'},
  duda: {signalKey: 'duda', label: 'Duda'},
  duel: {signalKey: 'duel', label: 'Duel'},
  dx: {signalKey: 'dx', label: 'Dx'},
  dynamicweb: {signalKey: 'dynamicweb', label: 'Dynamicweb'},
  dynatrace: {signalKey: 'dynatrace', label: 'Dynatrace'},
  'dynatrace-rum': {signalKey: 'dynatraceRum', label: 'Dynatrace Rum'},
  easydigitaldownloads: {
    signalKey: 'easydigitaldownloads',
    label: 'Easydigitaldownloads',
  },
  easyengine: {signalKey: 'easyengine', label: 'Easyengine'},
  easygift: {signalKey: 'easygift', label: 'Easygift'},
  echarts: {signalKey: 'echarts', label: 'Echarts'},
  ecovium: {signalKey: 'ecovium', label: 'Ecovium'},
  ecwid: {signalKey: 'ecwid', label: 'Ecwid'},
  edgecast: {signalKey: 'edgecast', label: 'Edgecast'},
  edgio: {signalKey: 'edgio', label: 'Edgio'},
  'ef-js': {signalKey: 'efJs', label: 'Ef Js'},
  efilli: {signalKey: 'efilli', label: 'Efilli'},
  ekomi: {signalKey: 'ekomi', label: 'Ekomi'},
  'ektron-cms': {signalKey: 'ektronCms', label: 'Ektron Cms'},
  'elastic-apm': {signalKey: 'elasticApm', label: 'Elastic Apm'},
  elasticpress: {signalKey: 'elasticpress', label: 'Elasticpress'},
  elasticsearch: {signalKey: 'elasticsearch', label: 'Elasticsearch'},
  elasticsuite: {signalKey: 'elasticsuite', label: 'Elasticsuite'},
  'element-ui': {signalKey: 'elementUi', label: 'Element Ui'},
  'elementor-cloud': {signalKey: 'elementorCloud', label: 'Elementor Cloud'},
  'elementor-header-footer-builder': {
    signalKey: 'elementorHeaderFooterBuilder',
    label: 'Elementor Header Footer Builder',
  },
  elementskit: {signalKey: 'elementskit', label: 'Elementskit'},
  elevar: {signalKey: 'elevar', label: 'Elevar'},
  elfsight: {signalKey: 'elfsight', label: 'Elfsight'},
  elixir: {signalKey: 'elixir', label: 'Elixir'},
  'ellucian-crm-recruit': {
    signalKey: 'ellucianCrmRecruit',
    label: 'Ellucian Crm Recruit',
  },
  emailjs: {signalKey: 'emailjs', label: 'Emailjs'},
  embedly: {signalKey: 'embedly', label: 'Embedly'},
  embedplus: {signalKey: 'embedplus', label: 'Embedplus'},
  embedsocial: {signalKey: 'embedsocial', label: 'Embedsocial'},
  'ember-js': {signalKey: 'emberJs', label: 'Ember Js'},
  emblue: {signalKey: 'emblue', label: 'Emblue'},
  emotion: {signalKey: 'emotion', label: 'Emotion'},
  emotive: {signalKey: 'emotive', label: 'Emotive'},
  enable: {signalKey: 'enable', label: 'Enable'},
  enamad: {signalKey: 'enamad', label: 'Enamad'},
  ensighten: {signalKey: 'ensighten', label: 'Ensighten'},
  equalweb: {signalKey: 'equalweb', label: 'Equalweb'},
  'eraofecom-winads': {signalKey: 'eraofecomWinads', label: 'Eraofecom Winads'},
  erlang: {signalKey: 'erlang', label: 'Erlang'},
  errorception: {signalKey: 'errorception', label: 'Errorception'},
  eshopcrm: {signalKey: 'eshopcrm', label: 'Eshopcrm'},
  esputnik: {signalKey: 'esputnik', label: 'Esputnik'},
  'essential-accessibility': {
    signalKey: 'essentialAccessibility',
    label: 'Essential Accessibility',
  },
  'essential-addons-for-elementor': {
    signalKey: 'essentialAddonsForElementor',
    label: 'Essential Addons For Elementor',
  },
  esw: {signalKey: 'esw', label: 'Esw'},
  ethers: {signalKey: 'ethers', label: 'Ethers'},
  etix: {signalKey: 'etix', label: 'Etix'},
  etracker: {signalKey: 'etracker', label: 'Etracker'},
  'eucookie-eu': {signalKey: 'eucookieEu', label: 'Eucookie Eu'},
  eventon: {signalKey: 'eventon', label: 'Eventon'},
  everflow: {signalKey: 'everflow', label: 'Everflow'},
  everyaction: {signalKey: 'everyaction', label: 'Everyaction'},
  evidon: {signalKey: 'evidon', label: 'Evidon'},
  'eway-payments': {signalKey: 'ewayPayments', label: 'Eway Payments'},
  'ewww-image-optimizer': {
    signalKey: 'ewwwImageOptimizer',
    label: 'Ewww Image Optimizer',
  },
  'ex-co': {signalKey: 'exCo', label: 'Ex Co'},
  exactmetrics: {signalKey: 'exactmetrics', label: 'Exactmetrics'},
  exhibit: {signalKey: 'exhibit', label: 'Exhibit'},
  exitintel: {signalKey: 'exitintel', label: 'Exitintel'},
  expertrec: {signalKey: 'expertrec', label: 'Expertrec'},
  express: {signalKey: 'express', label: 'Express'},
  expressionengine: {signalKey: 'expressionengine', label: 'Expressionengine'},
  'extendthemes-highlight': {
    signalKey: 'extendthemesHighlight',
    label: 'Extendthemes Highlight',
  },
  'extendthemes-materialis': {
    signalKey: 'extendthemesMaterialis',
    label: 'Extendthemes Materialis',
  },
  'extendthemes-mesmerize': {
    signalKey: 'extendthemesMesmerize',
    label: 'Extendthemes Mesmerize',
  },
  extjs: {signalKey: 'extjs', label: 'Extjs'},
  extole: {signalKey: 'extole', label: 'Extole'},
  ezoic: {signalKey: 'ezoic', label: 'Ezoic'},
  'f5-bigip': {signalKey: 'f5Bigip', label: 'F5 Bigip'},
  'facebook-ads': {signalKey: 'facebookAds', label: 'Facebook Ads'},
  'facebook-chat-plugin': {
    signalKey: 'facebookChatPlugin',
    label: 'Facebook Chat Plugin',
  },
  'facebook-login': {signalKey: 'facebookLogin', label: 'Facebook Login'},
  'facil-iti': {signalKey: 'facilIti', label: 'Facil Iti'},
  'famethemes-onepress': {
    signalKey: 'famethemesOnepress',
    label: 'Famethemes Onepress',
  },
  fancybox: {signalKey: 'fancybox', label: 'Fancybox'},
  fanplayr: {signalKey: 'fanplayr', label: 'Fanplayr'},
  fareharbor: {signalKey: 'fareharbor', label: 'Fareharbor'},
  'fast-bundle': {signalKey: 'fastBundle', label: 'Fast Bundle'},
  fastcomet: {signalKey: 'fastcomet', label: 'Fastcomet'},
  fasterize: {signalKey: 'fasterize', label: 'Fasterize'},
  fastspring: {signalKey: 'fastspring', label: 'Fastspring'},
  fathom: {signalKey: 'fathom', label: 'Fathom'},
  fedex: {signalKey: 'fedex', label: 'Fedex'},
  fedora: {signalKey: 'fedora', label: 'Fedora'},
  feefo: {signalKey: 'feefo', label: 'Feefo'},
  fera: {signalKey: 'fera', label: 'Fera'},
  filepond: {signalKey: 'filepond', label: 'Filepond'},
  findify: {signalKey: 'findify', label: 'Findify'},
  fingerprintjs: {signalKey: 'fingerprintjs', label: 'Fingerprintjs'},
  fireblade: {signalKey: 'fireblade', label: 'Fireblade'},
  firepush: {signalKey: 'firepush', label: 'Firepush'},
  firstpromoter: {signalKey: 'firstpromoter', label: 'Firstpromoter'},
  'fit-analytics': {signalKey: 'fitAnalytics', label: 'Fit Analytics'},
  flagsmith: {signalKey: 'flagsmith', label: 'Flagsmith'},
  flarum: {signalKey: 'flarum', label: 'Flarum'},
  flask: {signalKey: 'flask', label: 'Flask'},
  'flat-ui': {signalKey: 'flatUi', label: 'Flat Ui'},
  flexslider: {signalKey: 'flexslider', label: 'Flexslider'},
  flickity: {signalKey: 'flickity', label: 'Flickity'},
  flippingbook: {signalKey: 'flippingbook', label: 'Flippingbook'},
  flocktory: {signalKey: 'flocktory', label: 'Flocktory'},
  flourish: {signalKey: 'flourish', label: 'Flourish'},
  flow: {signalKey: 'flow', label: 'Flow'},
  flowbite: {signalKey: 'flowbite', label: 'Flowbite'},
  flowplayer: {signalKey: 'flowplayer', label: 'Flowplayer'},
  flutter: {signalKey: 'flutter', label: 'Flutter'},
  'fly-io': {signalKey: 'flyIo', label: 'Fly Io'},
  'flying-analytics': {signalKey: 'flyingAnalytics', label: 'Flying Analytics'},
  'flying-images': {signalKey: 'flyingImages', label: 'Flying Images'},
  'flying-pages': {signalKey: 'flyingPages', label: 'Flying Pages'},
  flyingpress: {signalKey: 'flyingpress', label: 'Flyingpress'},
  flywheel: {signalKey: 'flywheel', label: 'Flywheel'},
  fomo: {signalKey: 'fomo', label: 'Fomo'},
  'font-awesome': {signalKey: 'fontAwesome', label: 'Font Awesome'},
  'fooplugins-foogallery': {
    signalKey: 'foopluginsFoogallery',
    label: 'Fooplugins Foogallery',
  },
  'forethought-solve': {
    signalKey: 'forethoughtSolve',
    label: 'Forethought Solve',
  },
  formassembly: {signalKey: 'formassembly', label: 'Formassembly'},
  formbold: {signalKey: 'formbold', label: 'Formbold'},
  'formidable-form': {signalKey: 'formidableForm', label: 'Formidable Form'},
  forter: {signalKey: 'forter', label: 'Forter'},
  four: {signalKey: 'four', label: 'Four'},
  foursixty: {signalKey: 'foursixty', label: 'Foursixty'},
  'foxy-io': {signalKey: 'foxyIo', label: 'Foxy Io'},
  'framer-sites': {signalKey: 'framerSites', label: 'Framer Sites'},
  freebsd: {signalKey: 'freebsd', label: 'Freebsd'},
  fresco: {signalKey: 'fresco', label: 'Fresco'},
  freshchat: {signalKey: 'freshchat', label: 'Freshchat'},
  freshop: {signalKey: 'freshop', label: 'Freshop'},
  'freshworks-crm': {signalKey: 'freshworksCrm', label: 'Freshworks Crm'},
  friendbuy: {signalKey: 'friendbuy', label: 'Friendbuy'},
  'friendly-captcha': {signalKey: 'friendlyCaptcha', label: 'Friendly Captcha'},
  'froala-editor': {signalKey: 'froalaEditor', label: 'Froala Editor'},
  'front-chat': {signalKey: 'frontChat', label: 'Front Chat'},
  frontify: {signalKey: 'frontify', label: 'Frontify'},
  frontpage: {signalKey: 'frontpage', label: 'Frontpage'},
  fullcalendar: {signalKey: 'fullcalendar', label: 'Fullcalendar'},
  fullcontact: {signalKey: 'fullcontact', label: 'Fullcontact'},
  'fullpage-js': {signalKey: 'fullpageJs', label: 'Fullpage Js'},
  'funding-choices': {signalKey: 'fundingChoices', label: 'Funding Choices'},
  'fundraise-up': {signalKey: 'fundraiseUp', label: 'Fundraise Up'},
  funraise: {signalKey: 'funraise', label: 'Funraise'},
  fusioncharts: {signalKey: 'fusioncharts', label: 'Fusioncharts'},
  futurio: {signalKey: 'futurio', label: 'Futurio'},
  gatsby: {signalKey: 'gatsby', label: 'Gatsby'},
  'gatsby-cloud-image-cdn': {
    signalKey: 'gatsbyCloudImageCdn',
    label: 'Gatsby Cloud Image Cdn',
  },
  gauges: {signalKey: 'gauges', label: 'Gauges'},
  geetest: {signalKey: 'geetest', label: 'Geetest'},
  gempages: {signalKey: 'gempages', label: 'Gempages'},
  generateblocks: {signalKey: 'generateblocks', label: 'Generateblocks'},
  generatepress: {signalKey: 'generatepress', label: 'Generatepress'},
  'generatepress-gp-premium': {
    signalKey: 'generatepressGpPremium',
    label: 'Generatepress Gp Premium',
  },
  'genesis-theme': {signalKey: 'genesisTheme', label: 'Genesis Theme'},
  'genesys-cloud': {signalKey: 'genesysCloud', label: 'Genesys Cloud'},
  geniee: {signalKey: 'geniee', label: 'Geniee'},
  gentoo: {signalKey: 'gentoo', label: 'Gentoo'},
  'geo-targetly': {signalKey: 'geoTargetly', label: 'Geo Targetly'},
  geodis: {signalKey: 'geodis', label: 'Geodis'},
  getbutton: {signalKey: 'getbutton', label: 'Getbutton'},
  getresponse: {signalKey: 'getresponse', label: 'Getresponse'},
  getsitecontrol: {signalKey: 'getsitecontrol', label: 'Getsitecontrol'},
  getsocial: {signalKey: 'getsocial', label: 'Getsocial'},
  getyourguide: {signalKey: 'getyourguide', label: 'Getyourguide'},
  ghost: {signalKey: 'ghost', label: 'Ghost'},
  gitbook: {signalKey: 'gitbook', label: 'Gitbook'},
  givecampus: {signalKey: 'givecampus', label: 'Givecampus'},
  givesmart: {signalKey: 'givesmart', label: 'Givesmart'},
  givewp: {signalKey: 'givewp', label: 'Givewp'},
  givingfuel: {signalKey: 'givingfuel', label: 'Givingfuel'},
  gladly: {signalKey: 'wappGladly', label: 'Gladly (Web)'},
  'glide-js': {signalKey: 'glideJs', label: 'Glide Js'},
  'glider-js': {signalKey: 'gliderJs', label: 'Glider Js'},
  'global-e': {signalKey: 'globalE', label: 'Global E'},
  'globo-form-builder': {
    signalKey: 'globoFormBuilder',
    label: 'Globo Form Builder',
  },
  'globo-pre-order': {signalKey: 'globoPreOrder', label: 'Globo Pre Order'},
  glopal: {signalKey: 'glopal', label: 'Glopal'},
  gls: {signalKey: 'gls', label: 'Gls'},
  glyphicons: {signalKey: 'glyphicons', label: 'Glyphicons'},
  gnuboard: {signalKey: 'gnuboard', label: 'Gnuboard'},
  go: {signalKey: 'go', label: 'Go'},
  'go-instore': {signalKey: 'goInstore', label: 'Go Instore'},
  goaffpro: {signalKey: 'goaffpro', label: 'Goaffpro'},
  gocache: {signalKey: 'gocache', label: 'Gocache'},
  godaddy: {signalKey: 'godaddy', label: 'Godaddy'},
  'godaddy-coblocks': {signalKey: 'godaddyCoblocks', label: 'Godaddy Coblocks'},
  'godaddy-domain-parking': {
    signalKey: 'godaddyDomainParking',
    label: 'Godaddy Domain Parking',
  },
  'godaddy-go': {signalKey: 'godaddyGo', label: 'Godaddy Go'},
  'godaddy-primer': {signalKey: 'godaddyPrimer', label: 'Godaddy Primer'},
  'godaddy-website-builder': {
    signalKey: 'godaddyWebsiteBuilder',
    label: 'Godaddy Website Builder',
  },
  'google-adsense': {signalKey: 'googleAdsense', label: 'Google Adsense'},
  'google-analytics-enhanced-ecommerce': {
    signalKey: 'googleAnalyticsEnhancedEcommerce',
    label: 'Google Analytics Enhanced Ecommerce',
  },
  'google-call-conversion-tracking': {
    signalKey: 'googleCallConversionTracking',
    label: 'Google Call Conversion Tracking',
  },
  'google-charts': {signalKey: 'googleCharts', label: 'Google Charts'},
  'google-cloud': {signalKey: 'googleCloud', label: 'Google Cloud'},
  'google-cloud-storage': {
    signalKey: 'googleCloudStorage',
    label: 'Google Cloud Storage',
  },
  'google-cloud-trace': {
    signalKey: 'googleCloudTrace',
    label: 'Google Cloud Trace',
  },
  'google-code-prettify': {
    signalKey: 'googleCodePrettify',
    label: 'Google Code Prettify',
  },
  'google-customer-reviews': {
    signalKey: 'googleCustomerReviews',
    label: 'Google Customer Reviews',
  },
  'google-font-api': {signalKey: 'googleFontApi', label: 'Google Font Api'},
  'google-forms': {signalKey: 'googleForms', label: 'Google Forms'},
  'google-hosted-libraries': {
    signalKey: 'googleHostedLibraries',
    label: 'Google Hosted Libraries',
  },
  'google-maps': {signalKey: 'googleMaps', label: 'Google Maps'},
  'google-pagespeed': {signalKey: 'googlePagespeed', label: 'Google Pagespeed'},
  'google-publisher-tag': {
    signalKey: 'googlePublisherTag',
    label: 'Google Publisher Tag',
  },
  'google-sign-in': {signalKey: 'googleSignIn', label: 'Google Sign In'},
  'google-sites': {signalKey: 'googleSites', label: 'Google Sites'},
  'google-tag-manager-for-wordpress': {
    signalKey: 'googleTagManagerForWordpress',
    label: 'Google Tag Manager For Wordpress',
  },
  'google-web-toolkit': {
    signalKey: 'googleWebToolkit',
    label: 'Google Web Toolkit',
  },
  gorgias: {signalKey: 'gorgias', label: 'Gorgias'},
  'gov-uk-frontend': {signalKey: 'govUkFrontend', label: 'Gov Uk Frontend'},
  'gpt-ai-power': {signalKey: 'gptAiPower', label: 'Gpt Ai Power'},
  grafana: {signalKey: 'grafana', label: 'Grafana'},
  'granim-js': {signalKey: 'granimJs', label: 'Granim Js'},
  grapesjs: {signalKey: 'grapesjs', label: 'Grapesjs'},
  graphcms: {signalKey: 'graphcms', label: 'Graphcms'},
  graphql: {signalKey: 'graphql', label: 'Graphql'},
  gravatar: {signalKey: 'gravatar', label: 'Gravatar'},
  'gravity-forms': {signalKey: 'gravityForms', label: 'Gravity Forms'},
  greenhouse: {signalKey: 'wappGreenhouse', label: 'Greenhouse (Web)'},
  gridsome: {signalKey: 'gridsome', label: 'Gridsome'},
  grin: {signalKey: 'grin', label: 'Grin'},
  groupby: {signalKey: 'groupby', label: 'Groupby'},
  growingio: {signalKey: 'growingio', label: 'Growingio'},
  gsap: {signalKey: 'gsap', label: 'Gsap'},
  gtranslate: {signalKey: 'gtranslate', label: 'Gtranslate'},
  'gtranslate-app': {signalKey: 'gtranslateApp', label: 'Gtranslate App'},
  gumgum: {signalKey: 'gumgum', label: 'Gumgum'},
  gumlet: {signalKey: 'gumlet', label: 'Gumlet'},
  gumroad: {signalKey: 'gumroad', label: 'Gumroad'},
  gunicorn: {signalKey: 'gunicorn', label: 'Gunicorn'},
  gutenberg: {signalKey: 'gutenberg', label: 'Gutenberg'},
  'hammer-js': {signalKey: 'hammerJs', label: 'Hammer Js'},
  handlebars: {signalKey: 'handlebars', label: 'Handlebars'},
  handtalk: {signalKey: 'handtalk', label: 'Handtalk'},
  'happy-returns': {signalKey: 'happyReturns', label: 'Happy Returns'},
  'happyfox-live-chat': {
    signalKey: 'happyfoxLiveChat',
    label: 'Happyfox Live Chat',
  },
  hashnode: {signalKey: 'hashnode', label: 'Hashnode'},
  'hashtag-labs': {signalKey: 'hashtagLabs', label: 'Hashtag Labs'},
  'hashthemes-total': {signalKey: 'hashthemesTotal', label: 'Hashthemes Total'},
  hcaptcha: {signalKey: 'hcaptcha', label: 'Hcaptcha'},
  'hcl-commerce': {signalKey: 'hclCommerce', label: 'Hcl Commerce'},
  'hcl-digital-experience': {
    signalKey: 'hclDigitalExperience',
    label: 'Hcl Digital Experience',
  },
  headjs: {signalKey: 'headjs', label: 'Headjs'},
  'headless-ui': {signalKey: 'headlessUi', label: 'Headless Ui'},
  'heartland-payment-systems': {
    signalKey: 'heartlandPaymentSystems',
    label: 'Heartland Payment Systems',
  },
  'helix-ultimate': {signalKey: 'helixUltimate', label: 'Helix Ultimate'},
  'helixo-ufe': {signalKey: 'helixoUfe', label: 'Helixo Ufe'},
  'hello-bar': {signalKey: 'helloBar', label: 'Hello Bar'},
  'hello-elementor': {signalKey: 'helloElementor', label: 'Hello Elementor'},
  'help-scout': {signalKey: 'helpScout', label: 'Help Scout'},
  here: {signalKey: 'here', label: 'Here'},
  hermes: {signalKey: 'hermes', label: 'Hermes'},
  hero: {signalKey: 'hero', label: 'Hero'},
  hestia: {signalKey: 'hestia', label: 'Hestia'},
  hetzner: {signalKey: 'hetzner', label: 'Hetzner'},
  hexo: {signalKey: 'hexo', label: 'Hexo'},
  'hextom-free-shipping-bar': {
    signalKey: 'hextomFreeShippingBar',
    label: 'Hextom Free Shipping Bar',
  },
  'hi-platform': {signalKey: 'hiPlatform', label: 'Hi Platform'},
  highcharts: {signalKey: 'highcharts', label: 'Highcharts'},
  highlevel: {signalKey: 'highlevel', label: 'Highlevel'},
  'highlight-js': {signalKey: 'highlightJs', label: 'Highlight Js'},
  highstock: {signalKey: 'highstock', label: 'Highstock'},
  hireology: {signalKey: 'hireology', label: 'Hireology'},
  histats: {signalKey: 'histats', label: 'Histats'},
  history: {signalKey: 'history', label: 'History'},
  hockeystack: {signalKey: 'hockeystack', label: 'Hockeystack'},
  'hoefler-co': {signalKey: 'hoeflerCo', label: 'Hoefler Co'},
  'hogan-js': {signalKey: 'hoganJs', label: 'Hogan Js'},
  homestead: {signalKey: 'homestead', label: 'Homestead'},
  honeybadger: {signalKey: 'honeybadger', label: 'Honeybadger'},
  hostinger: {signalKey: 'hostinger', label: 'Hostinger'},
  'hostinger-cdn': {signalKey: 'hostingerCdn', label: 'Hostinger Cdn'},
  hostmeapp: {signalKey: 'hostmeapp', label: 'Hostmeapp'},
  hostpoint: {signalKey: 'hostpoint', label: 'Hostpoint'},
  'hotjar-incoming-feedback': {
    signalKey: 'hotjarIncomingFeedback',
    label: 'Hotjar Incoming Feedback',
  },
  'howler-js': {signalKey: 'howlerJs', label: 'Howler Js'},
  hsts: {signalKey: 'hsts', label: 'Hsts'},
  htmx: {signalKey: 'htmx', label: 'Htmx'},
  'http-2': {signalKey: 'http2', label: 'Http 2'},
  'http-3': {signalKey: 'http3', label: 'Http 3'},
  'hubspot-analytics': {
    signalKey: 'hubspotAnalytics',
    label: 'Hubspot Analytics',
  },
  'hubspot-chat': {signalKey: 'hubspotChat', label: 'Hubspot Chat'},
  'hubspot-cookie-policy-banner': {
    signalKey: 'hubspotCookiePolicyBanner',
    label: 'Hubspot Cookie Policy Banner',
  },
  'hubspot-wordpress-plugin': {
    signalKey: 'hubspotWordpressPlugin',
    label: 'Hubspot Wordpress Plugin',
  },
  hugo: {signalKey: 'hugo', label: 'Hugo'},
  'hulkapps-form-builder': {
    signalKey: 'hulkappsFormBuilder',
    label: 'Hulkapps Form Builder',
  },
  'hulkapps-infinite-product-options': {
    signalKey: 'hulkappsInfiniteProductOptions',
    label: 'Hulkapps Infinite Product Options',
  },
  'human-presence': {signalKey: 'humanPresence', label: 'Human Presence'},
  'hund-io': {signalKey: 'hundIo', label: 'Hund Io'},
  hushly: {signalKey: 'hushly', label: 'Hushly'},
  hyperscript: {signalKey: 'hyperscript', label: 'Hyperscript'},
  hyros: {signalKey: 'hyros', label: 'Hyros'},
  'hyva-themes': {signalKey: 'hyvaThemes', label: 'Hyva Themes'},
  iadvize: {signalKey: 'iadvize', label: 'Iadvize'},
  id5: {signalKey: 'id5', label: 'Id5'},
  identrust: {signalKey: 'identrust', label: 'Identrust'},
  igodigital: {signalKey: 'igodigital', label: 'Igodigital'},
  'ihomefinder-idx': {signalKey: 'ihomefinderIdx', label: 'Ihomefinder Idx'},
  iis: {signalKey: 'iis', label: 'Iis'},
  'image-relay': {signalKey: 'imageRelay', label: 'Image Relay'},
  imageengine: {signalKey: 'imageengine', label: 'Imageengine'},
  'imagely-nextgen-gallery': {
    signalKey: 'imagelyNextgenGallery',
    label: 'Imagely Nextgen Gallery',
  },
  imgix: {signalKey: 'imgix', label: 'Imgix'},
  'immutable-js': {signalKey: 'immutableJs', label: 'Immutable Js'},
  imunify360: {signalKey: 'imunify360', label: 'Imunify360'},
  'in-cart-upsell-cross-sell': {
    signalKey: 'inCartUpsellCrossSell',
    label: 'In Cart Upsell Cross Sell',
  },
  'incident-io': {signalKey: 'incidentIo', label: 'Incident Io'},
  'index-exchange': {signalKey: 'indexExchange', label: 'Index Exchange'},
  'inertia-js': {signalKey: 'inertiaJs', label: 'Inertia Js'},
  infernojs: {signalKey: 'infernojs', label: 'Infernojs'},
  infogram: {signalKey: 'infogram', label: 'Infogram'},
  infolinks: {signalKey: 'infolinks', label: 'Infolinks'},
  'insightly-crm': {signalKey: 'insightlyCrm', label: 'Insightly Crm'},
  inspectlet: {signalKey: 'inspectlet', label: 'Inspectlet'},
  instabot: {signalKey: 'instabot', label: 'Instabot'},
  instafeed: {signalKey: 'instafeed', label: 'Instafeed'},
  instana: {signalKey: 'instana', label: 'Instana'},
  'instant-page': {signalKey: 'instantPage', label: 'Instant Page'},
  instantclick: {signalKey: 'instantclick', label: 'Instantclick'},
  instatus: {signalKey: 'instatus', label: 'Instatus'},
  insyncai: {signalKey: 'insyncai', label: 'Insyncai'},
  'integral-ad-science': {
    signalKey: 'integralAdScience',
    label: 'Integral Ad Science',
  },
  'intercom-articles': {
    signalKey: 'intercomArticles',
    label: 'Intercom Articles',
  },
  'intersection-observer': {
    signalKey: 'intersectionObserver',
    label: 'Intersection Observer',
  },
  invoca: {signalKey: 'invoca', label: 'Invoca'},
  ionic: {signalKey: 'ionic', label: 'Ionic'},
  ionicons: {signalKey: 'ionicons', label: 'Ionicons'},
  ionos: {signalKey: 'ionos', label: 'Ionos'},
  'ip-api': {signalKey: 'ipApi', label: 'Ip Api'},
  'ip2location-io': {signalKey: 'ip2locationIo', label: 'Ip2location Io'},
  ipapi: {signalKey: 'ipapiGeo', label: 'IPAPI'},
  ipdata: {signalKey: 'ipdata', label: 'Ipdata'},
  ipfs: {signalKey: 'ipfs', label: 'Ipfs'},
  ipgeolocation: {signalKey: 'ipgeolocation', label: 'Ipgeolocation'},
  ipify: {signalKey: 'ipify', label: 'Ipify'},
  ipinfo: {signalKey: 'ipinfo', label: 'Ipinfo'},
  ipstack: {signalKey: 'ipstack', label: 'Ipstack'},
  isotope: {signalKey: 'isotope', label: 'Isotope'},
  issuu: {signalKey: 'issuu', label: 'Issuu'},
  'ithemes-security': {signalKey: 'ithemesSecurity', label: 'Ithemes Security'},
  iubenda: {signalKey: 'iubenda', label: 'Iubenda'},
  'ivory-search': {signalKey: 'ivorySearch', label: 'Ivory Search'},
  izooto: {signalKey: 'izooto', label: 'Izooto'},
  j2store: {signalKey: 'j2store', label: 'J2store'},
  java: {signalKey: 'wappJava', label: 'Java (Web)'},
  'java-servlet': {signalKey: 'javaServlet', label: 'Java Servlet'},
  'javaserver-faces': {signalKey: 'javaserverFaces', label: 'Javaserver Faces'},
  'javaserver-pages': {signalKey: 'javaserverPages', label: 'Javaserver Pages'},
  'jboss-application-server': {
    signalKey: 'jbossApplicationServer',
    label: 'Jboss Application Server',
  },
  'jboss-web': {signalKey: 'jbossWeb', label: 'Jboss Web'},
  jcomponent: {signalKey: 'jcomponent', label: 'Jcomponent'},
  jekyll: {signalKey: 'jekyll', label: 'Jekyll'},
  jetpack: {signalKey: 'jetpack', label: 'Jetpack'},
  'jetpack-boost': {signalKey: 'jetpackBoost', label: 'Jetpack Boost'},
  jetty: {signalKey: 'jetty', label: 'Jetty'},
  jimdo: {signalKey: 'jimdo', label: 'Jimdo'},
  jivochat: {signalKey: 'jivochat', label: 'Jivochat'},
  jivox: {signalKey: 'jivox', label: 'Jivox'},
  jobvite: {signalKey: 'wappJobvite', label: 'Jobvite (Web)'},
  jplayer: {signalKey: 'jplayer', label: 'Jplayer'},
  jqplot: {signalKey: 'jqplot', label: 'Jqplot'},
  jquery: {signalKey: 'jquery', label: 'Jquery'},
  'jquery-cdn': {signalKey: 'jqueryCdn', label: 'Jquery Cdn'},
  'jquery-devbridge-autocomplete': {
    signalKey: 'jqueryDevbridgeAutocomplete',
    label: 'Jquery Devbridge Autocomplete',
  },
  'jquery-migrate': {signalKey: 'jqueryMigrate', label: 'Jquery Migrate'},
  'jquery-mobile': {signalKey: 'jqueryMobile', label: 'Jquery Mobile'},
  'jquery-modal': {signalKey: 'jqueryModal', label: 'Jquery Modal'},
  'jquery-pjax': {signalKey: 'jqueryPjax', label: 'Jquery Pjax'},
  'jquery-sparklines': {
    signalKey: 'jquerySparklines',
    label: 'Jquery Sparklines',
  },
  'jquery-ui': {signalKey: 'jqueryUi', label: 'Jquery Ui'},
  jsdelivr: {signalKey: 'jsdelivr', label: 'Jsdelivr'},
  jsobservable: {signalKey: 'jsobservable', label: 'Jsobservable'},
  jsrender: {signalKey: 'jsrender', label: 'Jsrender'},
  jss: {signalKey: 'jss', label: 'Jss'},
  jsviews: {signalKey: 'jsviews', label: 'Jsviews'},
  'judge-me': {signalKey: 'judgeMe', label: 'Judge Me'},
  junip: {signalKey: 'junip', label: 'Junip'},
  justuno: {signalKey: 'justuno', label: 'Justuno'},
  'justuno-app': {signalKey: 'justunoApp', label: 'Justuno App'},
  'jw-player': {signalKey: 'jwPlayer', label: 'Jw Player'},
  k2: {signalKey: 'k2', label: 'K2'},
  'kadence-wp-blocks': {
    signalKey: 'kadenceWpBlocks',
    label: 'Kadence Wp Blocks',
  },
  'kadence-wp-kadence': {
    signalKey: 'kadenceWpKadence',
    label: 'Kadence Wp Kadence',
  },
  'kaira-vogue': {signalKey: 'kairaVogue', label: 'Kaira Vogue'},
  kajabi: {signalKey: 'kajabi', label: 'Kajabi'},
  kakao: {signalKey: 'kakao', label: 'Kakao'},
  kaltura: {signalKey: 'kaltura', label: 'Kaltura'},
  kameleoon: {signalKey: 'kameleoon', label: 'Kameleoon'},
  kartra: {signalKey: 'kartra', label: 'Kartra'},
  kasada: {signalKey: 'kasada', label: 'Kasada'},
  katex: {signalKey: 'katex', label: 'Katex'},
  'keen-slider': {signalKey: 'keenSlider', label: 'Keen Slider'},
  'kendo-ui': {signalKey: 'kendoUi', label: 'Kendo Ui'},
  'kentico-cms': {signalKey: 'kenticoCms', label: 'Kentico Cms'},
  kestrel: {signalKey: 'kestrel', label: 'Kestrel'},
  ketch: {signalKey: 'ketch', label: 'Ketch'},
  kevel: {signalKey: 'kevel', label: 'Kevel'},
  keybase: {signalKey: 'keybase', label: 'Keybase'},
  keycdn: {signalKey: 'keycdn', label: 'Keycdn'},
  'kibo-commerce': {signalKey: 'kiboCommerce', label: 'Kibo Commerce'},
  'kibo-personalization': {
    signalKey: 'kiboPersonalization',
    label: 'Kibo Personalization',
  },
  kindful: {signalKey: 'kindful', label: 'Kindful'},
  kinsta: {signalKey: 'kinsta', label: 'Kinsta'},
  'kirki-customizer-framework': {
    signalKey: 'kirkiCustomizerFramework',
    label: 'Kirki Customizer Framework',
  },
  'kiwi-sizing': {signalKey: 'kiwiSizing', label: 'Kiwi Sizing'},
  'klarna-checkout': {signalKey: 'klarnaCheckout', label: 'Klarna Checkout'},
  klaro: {signalKey: 'klaro', label: 'Klaro'},
  klevu: {signalKey: 'klevu', label: 'Klevu'},
  'knockout-js': {signalKey: 'knockoutJs', label: 'Knockout Js'},
  'ko-fi': {signalKey: 'koFi', label: 'Ko Fi'},
  koala: {signalKey: 'koala', label: 'Koala'},
  konduto: {signalKey: 'konduto', label: 'Konduto'},
  kong: {signalKey: 'kong', label: 'Kong'},
  'kontent-ai': {signalKey: 'kontentAi', label: 'Kontent Ai'},
  kount: {signalKey: 'kount', label: 'Kount'},
  'kwai-pixel': {signalKey: 'kwaiPixel', label: 'Kwai Pixel'},
  lagoon: {signalKey: 'lagoon', label: 'Lagoon'},
  landbot: {signalKey: 'landbot', label: 'Landbot'},
  langify: {signalKey: 'langify', label: 'Langify'},
  langshop: {signalKey: 'langshop', label: 'Langshop'},
  laravel: {signalKey: 'laravel', label: 'Laravel'},
  'laravel-echo': {signalKey: 'laravelEcho', label: 'Laravel Echo'},
  latitudepay: {signalKey: 'latitudepay', label: 'Latitudepay'},
  layouthub: {signalKey: 'layouthub', label: 'Layouthub'},
  lazysizes: {signalKey: 'lazysizes', label: 'Lazysizes'},
  'lazysizes-unveilhooks-plugin': {
    signalKey: 'lazysizesUnveilhooksPlugin',
    label: 'Lazysizes Unveilhooks Plugin',
  },
  leadinfo: {signalKey: 'leadinfo', label: 'Leadinfo'},
  leadster: {signalKey: 'leadster', label: 'Leadster'},
  leaflet: {signalKey: 'leaflet', label: 'Leaflet'},
  learndash: {signalKey: 'learndash', label: 'Learndash'},
  'legal-monster': {signalKey: 'legalMonster', label: 'Legal Monster'},
  lenis: {signalKey: 'lenis', label: 'Lenis'},
  less: {signalKey: 'less', label: 'Less'},
  'let-s-encrypt': {signalKey: 'letSEncrypt', label: "Let's Encrypt"},
  lever: {signalKey: 'wappLever', label: 'Lever (Web)'},
  libphonenumber: {signalKey: 'libphonenumber', label: 'Libphonenumber'},
  liferay: {signalKey: 'liferay', label: 'Liferay'},
  lightbox: {signalKey: 'lightbox', label: 'Lightbox'},
  lightning: {signalKey: 'lightning', label: 'Lightning'},
  limespot: {signalKey: 'limespot', label: 'Limespot'},
  'limit-login-attempts-reloaded': {
    signalKey: 'limitLoginAttemptsReloaded',
    label: 'Limit Login Attempts Reloaded',
  },
  'linkedin-sign-in': {signalKey: 'linkedinSignIn', label: 'Linkedin Sign In'},
  'linx-commerce': {signalKey: 'linxCommerce', label: 'Linx Commerce'},
  'linx-impulse': {signalKey: 'linxImpulse', label: 'Linx Impulse'},
  'liquid-web': {signalKey: 'liquidWeb', label: 'Liquid Web'},
  'list-js': {signalKey: 'listJs', label: 'List Js'},
  'lit-element': {signalKey: 'litElement', label: 'Lit Element'},
  'lit-html': {signalKey: 'litHtml', label: 'Lit Html'},
  'lite-youtube-embed': {
    signalKey: 'liteYoutubeEmbed',
    label: 'Lite Youtube Embed',
  },
  litespeed: {signalKey: 'litespeed', label: 'Litespeed'},
  lithium: {signalKey: 'lithium', label: 'Lithium'},
  littledata: {signalKey: 'littledata', label: 'Littledata'},
  liveagent: {signalKey: 'liveagent', label: 'Liveagent'},
  livecanvas: {signalKey: 'livecanvas', label: 'Livecanvas'},
  livechat: {signalKey: 'livechat', label: 'Livechat'},
  livefyre: {signalKey: 'livefyre', label: 'Livefyre'},
  liveintent: {signalKey: 'liveintent', label: 'Liveintent'},
  liveperson: {signalKey: 'wappLiveperson', label: 'LivePerson (Web)'},
  'liveramp-dpm': {signalKey: 'liverampDpm', label: 'Liveramp Dpm'},
  'liveramp-pcm': {signalKey: 'liverampPcm', label: 'Liveramp Pcm'},
  livescale: {signalKey: 'livescale', label: 'Livescale'},
  livesession: {signalKey: 'livesession', label: 'Livesession'},
  livewire: {signalKey: 'livewire', label: 'Livewire'},
  livezilla: {signalKey: 'livezilla', label: 'Livezilla'},
  'loadable-components': {
    signalKey: 'loadableComponents',
    label: 'Loadable Components',
  },
  locksmith: {signalKey: 'locksmith', label: 'Locksmith'},
  'locomotive-scroll': {
    signalKey: 'locomotiveScroll',
    label: 'Locomotive Scroll',
  },
  lodash: {signalKey: 'lodash', label: 'Lodash'},
  loggly: {signalKey: 'loggly', label: 'Loggly'},
  'login-with-amazon': {
    signalKey: 'loginWithAmazon',
    label: 'Login With Amazon',
  },
  loginradius: {signalKey: 'loginradius', label: 'Loginradius'},
  logrocket: {signalKey: 'logrocket', label: 'Logrocket'},
  loom: {signalKey: 'loom', label: 'Loom'},
  'loop-returns': {signalKey: 'loopReturns', label: 'Loop Returns'},
  loox: {signalKey: 'loox', label: 'Loox'},
  loqate: {signalKey: 'loqate', label: 'Loqate'},
  lottiefiles: {signalKey: 'lottiefiles', label: 'Lottiefiles'},
  lou: {signalKey: 'lou', label: 'Lou'},
  loyaltylion: {signalKey: 'loyaltylion', label: 'Loyaltylion'},
  'lozad-js': {signalKey: 'lozadJs', label: 'Lozad Js'},
  lua: {signalKey: 'lua', label: 'Lua'},
  'lucky-orange': {signalKey: 'luckyOrange', label: 'Lucky Orange'},
  luna: {signalKey: 'luna', label: 'Luna'},
  macaron: {signalKey: 'macaron', label: 'Macaron'},
  'machothemes-newsmag': {
    signalKey: 'machothemesNewsmag',
    label: 'Machothemes Newsmag',
  },
  'madcap-software': {signalKey: 'madcapSoftware', label: 'Madcap Software'},
  'mageworx-search-autocomplete': {
    signalKey: 'mageworxSearchAutocomplete',
    label: 'Mageworx Search Autocomplete',
  },
  magixite: {signalKey: 'magixite', label: 'Magixite'},
  'mailchimp-for-woocommerce': {
    signalKey: 'mailchimpForWoocommerce',
    label: 'Mailchimp For Woocommerce',
  },
  'mailchimp-for-wordpress': {
    signalKey: 'mailchimpForWordpress',
    label: 'Mailchimp For Wordpress',
  },
  mailerlite: {signalKey: 'mailerlite', label: 'Mailerlite'},
  'mailerlite-plugin': {
    signalKey: 'mailerlitePlugin',
    label: 'Mailerlite Plugin',
  },
  mailjet: {signalKey: 'mailjet', label: 'Mailjet'},
  mailmunch: {signalKey: 'mailmunch', label: 'Mailmunch'},
  mantine: {signalKey: 'mantine', label: 'Mantine'},
  manychat: {signalKey: 'manychat', label: 'Manychat'},
  'mapbox-gl-js': {signalKey: 'mapboxGlJs', label: 'Mapbox Gl Js'},
  'mapbox-js': {signalKey: 'mapboxJs', label: 'Mapbox Js'},
  'maplibre-gl-js': {signalKey: 'maplibreGlJs', label: 'Maplibre Gl Js'},
  mapplic: {signalKey: 'mapplic', label: 'Mapplic'},
  marchex: {signalKey: 'marchex', label: 'Marchex'},
  marfeel: {signalKey: 'marfeel', label: 'Marfeel'},
  mariadb: {signalKey: 'mariadb', label: 'Mariadb'},
  'marionette-js': {signalKey: 'marionetteJs', label: 'Marionette Js'},
  marked: {signalKey: 'marked', label: 'Marked'},
  marker: {signalKey: 'marker', label: 'Marker'},
  'marketo-forms': {signalKey: 'marketoForms', label: 'Marketo Forms'},
  marko: {signalKey: 'marko', label: 'Marko'},
  'master-slider': {signalKey: 'masterSlider', label: 'Master Slider'},
  'master-slider-plugin': {
    signalKey: 'masterSliderPlugin',
    label: 'Master Slider Plugin',
  },
  mastercard: {signalKey: 'mastercard', label: 'Mastercard'},
  'material-design-lite': {
    signalKey: 'materialDesignLite',
    label: 'Material Design Lite',
  },
  'materialize-css': {signalKey: 'materializeCss', label: 'Materialize Css'},
  'math-js': {signalKey: 'mathJs', label: 'Math Js'},
  mathjax: {signalKey: 'mathjax', label: 'Mathjax'},
  'matomo-analytics': {signalKey: 'matomoAnalytics', label: 'Matomo Analytics'},
  'matomo-tag-manager': {
    signalKey: 'matomoTagManager',
    label: 'Matomo Tag Manager',
  },
  mautic: {signalKey: 'mautic', label: 'Mautic'},
  maxmind: {signalKey: 'maxmind', label: 'Maxmind'},
  mdbootstrap: {signalKey: 'mdbootstrap', label: 'Mdbootstrap'},
  measured: {signalKey: 'measured', label: 'Measured'},
  medallia: {signalKey: 'medallia', label: 'Medallia'},
  'media-net': {signalKey: 'mediaNet', label: 'Media Net'},
  'mediaelement-js': {signalKey: 'mediaelementJs', label: 'Mediaelement Js'},
  mediavine: {signalKey: 'mediavine', label: 'Mediavine'},
  mediawiki: {signalKey: 'mediawiki', label: 'Mediawiki'},
  medium: {signalKey: 'medium', label: 'Medium'},
  meilisearch: {signalKey: 'meilisearch', label: 'Meilisearch'},
  memberstack: {signalKey: 'memberstack', label: 'Memberstack'},
  'mercado-shops': {signalKey: 'mercadoShops', label: 'Mercado Shops'},
  mermaid: {signalKey: 'mermaid', label: 'Mermaid'},
  metaslider: {signalKey: 'metaslider', label: 'Metaslider'},
  meteor: {signalKey: 'meteor', label: 'Meteor'},
  metismenu: {signalKey: 'metismenu', label: 'Metismenu'},
  metroui: {signalKey: 'metroui', label: 'Metroui'},
  mgid: {signalKey: 'mgid', label: 'Mgid'},
  'microsoft-ajax-content-delivery-network': {
    signalKey: 'microsoftAjaxContentDeliveryNetwork',
    label: 'Microsoft Ajax Content Delivery Network',
  },
  'microsoft-application-insights': {
    signalKey: 'microsoftApplicationInsights',
    label: 'Microsoft Application Insights',
  },
  'microsoft-asp-net': {
    signalKey: 'microsoftAspNet',
    label: 'Microsoft Asp Net',
  },
  'microsoft-authentication': {
    signalKey: 'microsoftAuthentication',
    label: 'Microsoft Authentication',
  },
  'microsoft-clarity': {
    signalKey: 'microsoftClarity',
    label: 'Microsoft Clarity',
  },
  'microsoft-httpapi': {
    signalKey: 'microsoftHttpapi',
    label: 'Microsoft Httpapi',
  },
  'microsoft-sharepoint': {
    signalKey: 'microsoftSharepoint',
    label: 'Microsoft Sharepoint',
  },
  'microsoft-visual-studio': {
    signalKey: 'microsoftVisualStudio',
    label: 'Microsoft Visual Studio',
  },
  'microsoft-word': {signalKey: 'microsoftWord', label: 'Microsoft Word'},
  mindbody: {signalKey: 'mindbody', label: 'Mindbody'},
  minmaxify: {signalKey: 'minmaxify', label: 'Minmaxify'},
  mintlify: {signalKey: 'mintlify', label: 'Mintlify'},
  miva: {signalKey: 'miva', label: 'Miva'},
  mkdocs: {signalKey: 'mkdocs', label: 'Mkdocs'},
  mobirise: {signalKey: 'mobirise', label: 'Mobirise'},
  mobx: {signalKey: 'mobx', label: 'Mobx'},
  'mod-fastcgi': {signalKey: 'modFastcgi', label: 'Mod Fastcgi'},
  'mod-jk': {signalKey: 'modJk', label: 'Mod Jk'},
  'mod-perl': {signalKey: 'modPerl', label: 'Mod Perl'},
  'mod-ssl': {signalKey: 'modSsl', label: 'Mod Ssl'},
  'mod-wsgi': {signalKey: 'modWsgi', label: 'Mod Wsgi'},
  'model-viewer': {signalKey: 'modelViewer', label: 'Model Viewer'},
  modernizr: {signalKey: 'modernizr', label: 'Modernizr'},
  'module-federation': {
    signalKey: 'moduleFederation',
    label: 'Module Federation',
  },
  'moguta-cms': {signalKey: 'mogutaCms', label: 'Moguta Cms'},
  'moment-js': {signalKey: 'momentJs', label: 'Moment Js'},
  'moment-timezone': {signalKey: 'momentTimezone', label: 'Moment Timezone'},
  'monaco-editor': {signalKey: 'monacoEditor', label: 'Monaco Editor'},
  'mondial-relay': {signalKey: 'mondialRelay', label: 'Mondial Relay'},
  moneris: {signalKey: 'moneris', label: 'Moneris'},
  'moneris-payment-gateway': {
    signalKey: 'monerisPaymentGateway',
    label: 'Moneris Payment Gateway',
  },
  monetate: {signalKey: 'monetate', label: 'Monetate'},
  mongodb: {signalKey: 'wappMongodb', label: 'MongoDB (Web)'},
  'mono-net': {signalKey: 'monoNet', label: 'Mono Net'},
  monsterinsights: {signalKey: 'monsterinsights', label: 'Monsterinsights'},
  moodle: {signalKey: 'moodle', label: 'Moodle'},
  mootools: {signalKey: 'mootools', label: 'Mootools'},
  'moove-gdpr-consent': {
    signalKey: 'mooveGdprConsent',
    label: 'Moove Gdpr Consent',
  },
  mopinion: {signalKey: 'mopinion', label: 'Mopinion'},
  motocms: {signalKey: 'motocms', label: 'Motocms'},
  'mouse-flow': {signalKey: 'mouseFlow', label: 'Mouse Flow'},
  mrw: {signalKey: 'mrw', label: 'Mrw'},
  mtcaptcha: {signalKey: 'mtcaptcha', label: 'Mtcaptcha'},
  mui: {signalKey: 'mui', label: 'Mui'},
  mulberry: {signalKey: 'mulberry', label: 'Mulberry'},
  'mura-cms': {signalKey: 'muraCms', label: 'Mura Cms'},
  mustache: {signalKey: 'mustache', label: 'Mustache'},
  muuri: {signalKey: 'muuri', label: 'Muuri'},
  myfonts: {signalKey: 'myfonts', label: 'Myfonts'},
  mylivechat: {signalKey: 'mylivechat', label: 'Mylivechat'},
  mywebsite: {signalKey: 'mywebsite', label: 'Mywebsite'},
  'mywebsite-creator': {
    signalKey: 'mywebsiteCreator',
    label: 'Mywebsite Creator',
  },
  nagich: {signalKey: 'nagich', label: 'Nagich'},
  narrativ: {signalKey: 'narrativ', label: 'Narrativ'},
  narvar: {signalKey: 'narvar', label: 'Narvar'},
  nationbuilder: {signalKey: 'nationbuilder', label: 'Nationbuilder'},
  nativo: {signalKey: 'nativo', label: 'Nativo'},
  navegg: {signalKey: 'navegg', label: 'Navegg'},
  'naver-analytics': {signalKey: 'naverAnalytics', label: 'Naver Analytics'},
  'neon-crm': {signalKey: 'neonCrm', label: 'Neon Crm'},
  'neos-cms': {signalKey: 'neosCms', label: 'Neos Cms'},
  'neos-flow': {signalKey: 'neosFlow', label: 'Neos Flow'},
  nestify: {signalKey: 'nestify', label: 'Nestify'},
  'netcore-cloud': {signalKey: 'netcoreCloud', label: 'Netcore Cloud'},
  'netlify-forms': {signalKey: 'netlifyForms', label: 'Netlify Forms'},
  netsuite: {signalKey: 'wappNetsuite', label: 'Netsuite (Web)'},
  'nette-framework': {signalKey: 'netteFramework', label: 'Nette Framework'},
  'network-for-good': {signalKey: 'networkForGood', label: 'Network For Good'},
  neve: {signalKey: 'neve', label: 'Neve'},
  nexcess: {signalKey: 'nexcess', label: 'Nexcess'},
  'next-js': {signalKey: 'nextJs', label: 'Next Js'},
  'nextauth-js': {signalKey: 'nextauthJs', label: 'Nextauth Js'},
  'nextdoor-ads': {signalKey: 'nextdoorAds', label: 'Nextdoor Ads'},
  'nextgen-gallery': {signalKey: 'nextgenGallery', label: 'Nextgen Gallery'},
  nextra: {signalKey: 'nextra', label: 'Nextra'},
  nextsale: {signalKey: 'nextsale', label: 'Nextsale'},
  nextui: {signalKey: 'nextui', label: 'Nextui'},
  nicepage: {signalKey: 'nicepage', label: 'Nicepage'},
  nift: {signalKey: 'nift', label: 'Nift'},
  'ninja-forms': {signalKey: 'ninjaForms', label: 'Ninja Forms'},
  nitropack: {signalKey: 'nitropack', label: 'Nitropack'},
  'node-js': {signalKey: 'nodeJs', label: 'Node Js'},
  nodebb: {signalKey: 'nodebb', label: 'Nodebb'},
  nofraud: {signalKey: 'nofraud', label: 'Nofraud'},
  nogin: {signalKey: 'nogin', label: 'Nogin'},
  nopcommerce: {signalKey: 'nopcommerce', label: 'Nopcommerce'},
  'norton-shopping-guarantee': {
    signalKey: 'nortonShoppingGuarantee',
    label: 'Norton Shopping Guarantee',
  },
  nosto: {signalKey: 'nosto', label: 'Nosto'},
  'nosto-visual-ugc': {signalKey: 'nostoVisualUgc', label: 'Nosto Visual Ugc'},
  notion: {signalKey: 'notion', label: 'Notion'},
  nudgify: {signalKey: 'nudgify', label: 'Nudgify'},
  'nuxt-js': {signalKey: 'nuxtJs', label: 'Nuxt Js'},
  nvd3: {signalKey: 'nvd3', label: 'Nvd3'},
  oceanwp: {signalKey: 'oceanwp', label: 'Oceanwp'},
  oct8ne: {signalKey: 'oct8ne', label: 'Oct8ne'},
  'octane-ai': {signalKey: 'octaneAi', label: 'Octane Ai'},
  'october-cms': {signalKey: 'octoberCms', label: 'October Cms'},
  odoo: {signalKey: 'odoo', label: 'Odoo'},
  okendo: {signalKey: 'okendo', label: 'Okendo'},
  olapic: {signalKey: 'olapic', label: 'Olapic'},
  olark: {signalKey: 'olark', label: 'Olark'},
  'omni-cms': {signalKey: 'omniCms', label: 'Omni Cms'},
  omniconvert: {signalKey: 'omniconvert', label: 'Omniconvert'},
  omnisend: {signalKey: 'omnisend', label: 'Omnisend'},
  'omnisend-email-marketing-sms': {
    signalKey: 'omnisendEmailMarketingSms',
    label: 'Omnisend Email Marketing Sms',
  },
  'omny-studio': {signalKey: 'omnyStudio', label: 'Omny Studio'},
  'one-com': {signalKey: 'oneCom', label: 'One Com'},
  oneall: {signalKey: 'oneall', label: 'Oneall'},
  onecause: {signalKey: 'onecause', label: 'Onecause'},
  'onepress-social-locker': {
    signalKey: 'onepressSocialLocker',
    label: 'Onepress Social Locker',
  },
  onfido: {signalKey: 'onfido', label: 'Onfido'},
  'ookla-speedtest-custom': {
    signalKey: 'ooklaSpeedtestCustom',
    label: 'Ookla Speedtest Custom',
  },
  'open-graph': {signalKey: 'openGraph', label: 'Open Graph'},
  'open-web-analytics': {
    signalKey: 'openWebAnalytics',
    label: 'Open Web Analytics',
  },
  opencities: {signalKey: 'opencities', label: 'Opencities'},
  opengse: {signalKey: 'opengse', label: 'Opengse'},
  openlayers: {signalKey: 'openlayers', label: 'Openlayers'},
  openresty: {signalKey: 'openresty', label: 'Openresty'},
  openssl: {signalKey: 'openssl', label: 'Openssl'},
  openstreetmap: {signalKey: 'openstreetmap', label: 'Openstreetmap'},
  opentable: {signalKey: 'opentable', label: 'Opentable'},
  openweb: {signalKey: 'openweb', label: 'Openweb'},
  openx: {signalKey: 'openx', label: 'Openx'},
  'opigno-lms': {signalKey: 'opignoLms', label: 'Opigno Lms'},
  opinionlab: {signalKey: 'opinionlab', label: 'Opinionlab'},
  optimise: {signalKey: 'optimise', label: 'Optimise'},
  'optimizely-content-management': {
    signalKey: 'optimizelyContentManagement',
    label: 'Optimizely Content Management',
  },
  optimonk: {signalKey: 'optimonk', label: 'Optimonk'},
  optimove: {signalKey: 'optimove', label: 'Optimove'},
  optinmonster: {signalKey: 'optinmonster', label: 'Optinmonster'},
  'optinmonster-plugin': {
    signalKey: 'optinmonsterPlugin',
    label: 'Optinmonster Plugin',
  },
  'oracle-bluekai': {signalKey: 'oracleBluekai', label: 'Oracle Bluekai'},
  'oracle-commerce': {signalKey: 'oracleCommerce', label: 'Oracle Commerce'},
  'oracle-commerce-cloud': {
    signalKey: 'oracleCommerceCloud',
    label: 'Oracle Commerce Cloud',
  },
  'oracle-dynamic-monitoring-service': {
    signalKey: 'oracleDynamicMonitoringService',
    label: 'Oracle Dynamic Monitoring Service',
  },
  'oracle-infinity': {signalKey: 'oracleInfinity', label: 'Oracle Infinity'},
  'oracle-maxymiser': {signalKey: 'oracleMaxymiser', label: 'Oracle Maxymiser'},
  'oracle-moat-measurement': {
    signalKey: 'oracleMoatMeasurement',
    label: 'Oracle Moat Measurement',
  },
  'oracle-recommendations-on-demand': {
    signalKey: 'oracleRecommendationsOnDemand',
    label: 'Oracle Recommendations On Demand',
  },
  'oracle-weblogic-server': {
    signalKey: 'oracleWeblogicServer',
    label: 'Oracle Weblogic Server',
  },
  orbitfox: {signalKey: 'orbitfox', label: 'Orbitfox'},
  'orchard-core': {signalKey: 'orchardCore', label: 'Orchard Core'},
  orckestra: {signalKey: 'orckestra', label: 'Orckestra'},
  ordergroove: {signalKey: 'ordergroove', label: 'Ordergroove'},
  'orderlogic-app': {signalKey: 'orderlogicApp', label: 'Orderlogic App'},
  osano: {signalKey: 'osano', label: 'Osano'},
  oscommerce: {signalKey: 'oscommerce', label: 'Oscommerce'},
  osticket: {signalKey: 'osticket', label: 'Osticket'},
  'outlook-web-app': {signalKey: 'outlookWebApp', label: 'Outlook Web App'},
  outsystems: {signalKey: 'outsystems', label: 'Outsystems'},
  'outtheboxthemes-panoramic': {
    signalKey: 'outtheboxthemesPanoramic',
    label: 'Outtheboxthemes Panoramic',
  },
  ovhcloud: {signalKey: 'ovhcloud', label: 'Ovhcloud'},
  'owl-carousel': {signalKey: 'owlCarousel', label: 'Owl Carousel'},
  'oxi-social-login': {signalKey: 'oxiSocialLogin', label: 'Oxi Social Login'},
  oxygen: {signalKey: 'oxygen', label: 'Oxygen'},
  'p5-js': {signalKey: 'p5Js', label: 'P5 Js'},
  paddle: {signalKey: 'paddle', label: 'Paddle'},
  'page-builder-framework': {
    signalKey: 'pageBuilderFramework',
    label: 'Page Builder Framework',
  },
  pagefly: {signalKey: 'pagefly', label: 'Pagefly'},
  pagely: {signalKey: 'pagely', label: 'Pagely'},
  panelbear: {signalKey: 'panelbear', label: 'Panelbear'},
  paradox: {signalKey: 'paradox', label: 'Paradox'},
  parcel: {signalKey: 'parcel', label: 'Parcel'},
  'parse-ly': {signalKey: 'parseLy', label: 'Parse Ly'},
  'particles-js': {signalKey: 'particlesJs', label: 'Particles Js'},
  partnerize: {signalKey: 'partnerize', label: 'Partnerize'},
  partytown: {signalKey: 'partytown', label: 'Partytown'},
  'paths-js': {signalKey: 'pathsJs', label: 'Paths Js'},
  patreon: {signalKey: 'patreon', label: 'Patreon'},
  paybright: {signalKey: 'paybright', label: 'Paybright'},
  paylocity: {signalKey: 'wappPaylocity', label: 'Paylocity (Web)'},
  'paypal-credit': {signalKey: 'paypalCredit', label: 'Paypal Credit'},
  'paypal-marketing-solutions': {
    signalKey: 'paypalMarketingSolutions',
    label: 'Paypal Marketing Solutions',
  },
  'pci-proxy': {signalKey: 'pciProxy', label: 'Pci Proxy'},
  pcrecruiter: {signalKey: 'pcrecruiter', label: 'Pcrecruiter'},
  'pdf-js': {signalKey: 'pdfJs', label: 'Pdf Js'},
  pebblepost: {signalKey: 'pebblepost', label: 'Pebblepost'},
  pepperjam: {signalKey: 'pepperjam', label: 'Pepperjam'},
  'perfectapps-swift': {
    signalKey: 'perfectappsSwift',
    label: 'Perfectapps Swift',
  },
  perfmatters: {signalKey: 'perfmatters', label: 'Perfmatters'},
  'performance-lab': {signalKey: 'performanceLab', label: 'Performance Lab'},
  perl: {signalKey: 'perl', label: 'Perl'},
  permutive: {signalKey: 'permutive', label: 'Permutive'},
  personaclick: {signalKey: 'personaclick', label: 'Personaclick'},
  personio: {signalKey: 'wappPersonio', label: 'Personio (Web)'},
  personizely: {signalKey: 'personizely', label: 'Personizely'},
  'petite-vue': {signalKey: 'petiteVue', label: 'Petite Vue'},
  phaser: {signalKey: 'phaser', label: 'Phaser'},
  phlox: {signalKey: 'phlox', label: 'Phlox'},
  phoenix: {signalKey: 'phoenix', label: 'Phoenix'},
  'phoenix-framework': {
    signalKey: 'phoenixFramework',
    label: 'Phoenix Framework',
  },
  'phoenix-liveview': {signalKey: 'phoenixLiveview', label: 'Phoenix Liveview'},
  'photo-gallery': {signalKey: 'photoGallery', label: 'Photo Gallery'},
  'photoshelter-for-brands': {
    signalKey: 'photoshelterForBrands',
    label: 'Photoshelter For Brands',
  },
  photoswipe: {signalKey: 'photoswipe', label: 'Photoswipe'},
  php: {signalKey: 'php', label: 'Php'},
  phpdebugbar: {signalKey: 'phpdebugbar', label: 'Phpdebugbar'},
  'phusion-passenger': {
    signalKey: 'phusionPassenger',
    label: 'Phusion Passenger',
  },
  piano: {signalKey: 'piano', label: 'Piano'},
  picreel: {signalKey: 'picreel', label: 'Picreel'},
  pimcore: {signalKey: 'pimcore', label: 'Pimcore'},
  'pingdom-rum': {signalKey: 'pingdomRum', label: 'Pingdom Rum'},
  pinterest: {signalKey: 'wappPinterest', label: 'Pinterest (Web)'},
  'pinterest-conversion-tag': {
    signalKey: 'pinterestConversionTag',
    label: 'Pinterest Conversion Tag',
  },
  pipedrive: {signalKey: 'pipedrive', label: 'Pipedrive'},
  'piwik-pro-core': {signalKey: 'piwikProCore', label: 'Piwik Pro Core'},
  pixelyoursite: {signalKey: 'pixelyoursite', label: 'Pixelyoursite'},
  pixijs: {signalKey: 'pixijs', label: 'Pixijs'},
  'pixlee-turnto': {signalKey: 'pixleeTurnto', label: 'Pixlee Turnto'},
  plaid: {signalKey: 'plaid', label: 'Plaid'},
  planet: {signalKey: 'planet', label: 'Planet'},
  plasmic: {signalKey: 'plasmic', label: 'Plasmic'},
  'platform-sh': {signalKey: 'platformSh', label: 'Platform Sh'},
  plausible: {signalKey: 'plausible', label: 'Plausible'},
  play: {signalKey: 'play', label: 'Play'},
  plesk: {signalKey: 'plesk', label: 'Plesk'},
  plotly: {signalKey: 'plotly', label: 'Plotly'},
  plyr: {signalKey: 'plyr', label: 'Plyr'},
  'po-st': {signalKey: 'poSt', label: 'Po St'},
  podsights: {signalKey: 'podsights', label: 'Podsights'},
  'pojo-me': {signalKey: 'pojoMe', label: 'Pojo Me'},
  polyfill: {signalKey: 'polyfill', label: 'Polyfill'},
  polylang: {signalKey: 'polylang', label: 'Polylang'},
  polymer: {signalKey: 'polymer', label: 'Polymer'},
  popmenu: {signalKey: 'popmenu', label: 'Popmenu'},
  popper: {signalKey: 'popper', label: 'Popper'},
  'popup-maker': {signalKey: 'popupMaker', label: 'Popup Maker'},
  'post-affiliate-pro': {
    signalKey: 'postAffiliatePro',
    label: 'Post Affiliate Pro',
  },
  postgresql: {signalKey: 'wappPostgresql', label: 'PostgreSQL (Web)'},
  postnl: {signalKey: 'postnl', label: 'Postnl'},
  postscript: {signalKey: 'postscript', label: 'Postscript'},
  potions: {signalKey: 'potions', label: 'Potions'},
  powr: {signalKey: 'powr', label: 'Powr'},
  preact: {signalKey: 'preact', label: 'Preact'},
  prebid: {signalKey: 'prebid', label: 'Prebid'},
  'prefix-free': {signalKey: 'prefixFree', label: 'Prefix Free'},
  'premio-chaty': {signalKey: 'premioChaty', label: 'Premio Chaty'},
  'press-customizr': {signalKey: 'pressCustomizr', label: 'Press Customizr'},
  'press-hueman': {signalKey: 'pressHueman', label: 'Press Hueman'},
  pressable: {signalKey: 'pressable', label: 'Pressable'},
  'pressmaximum-customify': {
    signalKey: 'pressmaximumCustomify',
    label: 'Pressmaximum Customify',
  },
  prestashop: {signalKey: 'prestashop', label: 'Prestashop'},
  prettyphoto: {signalKey: 'prettyphoto', label: 'Prettyphoto'},
  pricespider: {signalKey: 'pricespider', label: 'Pricespider'},
  primis: {signalKey: 'primis', label: 'Primis'},
  printful: {signalKey: 'printful', label: 'Printful'},
  'priority-hints': {signalKey: 'priorityHints', label: 'Priority Hints'},
  prism: {signalKey: 'prism', label: 'Prism'},
  prismic: {signalKey: 'prismic', label: 'Prismic'},
  privy: {signalKey: 'privy', label: 'Privy'},
  'privy-app': {signalKey: 'privyApp', label: 'Privy App'},
  processwire: {signalKey: 'processwire', label: 'Processwire'},
  'product-hunt': {signalKey: 'productHunt', label: 'Product Hunt'},
  profilepress: {signalKey: 'profilepress', label: 'Profilepress'},
  profitwell: {signalKey: 'profitwell', label: 'Profitwell'},
  'progress-sitefinity': {
    signalKey: 'progressSitefinity',
    label: 'Progress Sitefinity',
  },
  'proton-mail': {signalKey: 'protonMail', label: 'Proton Mail'},
  prototype: {signalKey: 'prototype', label: 'Prototype'},
  'provide-support': {signalKey: 'provideSupport', label: 'Provide Support'},
  pubmatic: {signalKey: 'pubmatic', label: 'Pubmatic'},
  pubsubjs: {signalKey: 'pubsubjs', label: 'Pubsubjs'},
  'pure-chat': {signalKey: 'pureChat', label: 'Pure Chat'},
  'pure-css': {signalKey: 'pureCss', label: 'Pure Css'},
  purecars: {signalKey: 'purecars', label: 'Purecars'},
  pushengage: {signalKey: 'pushengage', label: 'Pushengage'},
  pushnami: {signalKey: 'pushnami', label: 'Pushnami'},
  pushowl: {signalKey: 'pushowl', label: 'Pushowl'},
  'pushowl-web-push-notifications': {
    signalKey: 'pushowlWebPushNotifications',
    label: 'Pushowl Web Push Notifications',
  },
  pwa: {signalKey: 'pwa', label: 'Pwa'},
  pygments: {signalKey: 'pygments', label: 'Pygments'},
  pyrocms: {signalKey: 'pyrocms', label: 'Pyrocms'},
  python: {signalKey: 'wappPython', label: 'Python (Web)'},
  q4: {signalKey: 'q4', label: 'Q4'},
  'q4-cookie-monster': {
    signalKey: 'q4CookieMonster',
    label: 'Q4 Cookie Monster',
  },
  qgiv: {signalKey: 'qgiv', label: 'Qgiv'},
  qikify: {signalKey: 'qikify', label: 'Qikify'},
  qualaroo: {signalKey: 'qualaroo', label: 'Qualaroo'},
  'quantcast-choice': {signalKey: 'quantcastChoice', label: 'Quantcast Choice'},
  qubit: {signalKey: 'qubit', label: 'Qubit'},
  'quic-cloud': {signalKey: 'quicCloud', label: 'Quic Cloud'},
  quicklink: {signalKey: 'quicklink', label: 'Quicklink'},
  quill: {signalKey: 'quill', label: 'Quill'},
  quintype: {signalKey: 'quintype', label: 'Quintype'},
  rackcache: {signalKey: 'rackcache', label: 'Rackcache'},
  'radix-ui': {signalKey: 'radixUi', label: 'Radix Ui'},
  rain: {signalKey: 'rain', label: 'Rain'},
  raisedonors: {signalKey: 'raisedonors', label: 'Raisedonors'},
  rakuten: {signalKey: 'rakuten', label: 'Rakuten'},
  'rakuten-advertising': {
    signalKey: 'rakutenAdvertising',
    label: 'Rakuten Advertising',
  },
  ramda: {signalKey: 'ramda', label: 'Ramda'},
  'rankmath-seo': {signalKey: 'rankmathSeo', label: 'Rankmath Seo'},
  raphael: {signalKey: 'raphael', label: 'Raphael'},
  raptor: {signalKey: 'raptor', label: 'Raptor'},
  raygun: {signalKey: 'raygun', label: 'Raygun'},
  razorpay: {signalKey: 'razorpay', label: 'Razorpay'},
  'rd-station': {signalKey: 'rdStation', label: 'Rd Station'},
  're-amaze': {signalKey: 'reAmaze', label: 'Re Amaze'},
  'react-redux': {signalKey: 'reactRedux', label: 'React Redux'},
  readaloud: {signalKey: 'readaloud', label: 'Readaloud'},
  readme: {signalKey: 'readme', label: 'Readme'},
  readspeaker: {signalKey: 'readspeaker', label: 'Readspeaker'},
  readymag: {signalKey: 'readymag', label: 'Readymag'},
  rebelmouse: {signalKey: 'rebelmouse', label: 'Rebelmouse'},
  rebuy: {signalKey: 'rebuy', label: 'Rebuy'},
  'recaptcha-v2-for-contact-form-7': {
    signalKey: 'recaptchaV2ForContactForm7',
    label: 'Recaptcha V2 For Contact Form 7',
  },
  recapture: {signalKey: 'recapture', label: 'Recapture'},
  'recent-posts-widget-with-thumbnails': {
    signalKey: 'recentPostsWidgetWithThumbnails',
    label: 'Recent Posts Widget With Thumbnails',
  },
  recharts: {signalKey: 'recharts', label: 'Recharts'},
  'recite-me': {signalKey: 'reciteMe', label: 'Recite Me'},
  recovermycart: {signalKey: 'recovermycart', label: 'Recovermycart'},
  recruitee: {signalKey: 'wappRecruitee', label: 'Recruitee (Web)'},
  recurly: {signalKey: 'recurly', label: 'Recurly'},
  'red-hat': {signalKey: 'redHat', label: 'Red Hat'},
  redis: {signalKey: 'wappRedis', label: 'Redis (Web)'},
  'redis-object-cache': {
    signalKey: 'redisObjectCache',
    label: 'Redis Object Cache',
  },
  redmine: {signalKey: 'redmine', label: 'Redmine'},
  redoc: {signalKey: 'redoc', label: 'Redoc'},
  redux: {signalKey: 'redux', label: 'Redux'},
  'redux-framework': {signalKey: 'reduxFramework', label: 'Redux Framework'},
  redwoodjs: {signalKey: 'redwoodjs', label: 'Redwoodjs'},
  referralcandy: {signalKey: 'referralcandy', label: 'Referralcandy'},
  refersion: {signalKey: 'refersion', label: 'Refersion'},
  reflektion: {signalKey: 'reflektion', label: 'Reflektion'},
  remix: {signalKey: 'remix', label: 'Remix'},
  remixd: {signalKey: 'remixd', label: 'Remixd'},
  render: {signalKey: 'render', label: 'Render'},
  reputon: {signalKey: 'reputon', label: 'Reputon'},
  requirejs: {signalKey: 'requirejs', label: 'Requirejs'},
  'responsive-lightbox-gallery': {
    signalKey: 'responsiveLightboxGallery',
    label: 'Responsive Lightbox Gallery',
  },
  responsivevoice: {signalKey: 'responsivevoice', label: 'Responsivevoice'},
  resy: {signalKey: 'resy', label: 'Resy'},
  'retail-rocket': {signalKey: 'retailRocket', label: 'Retail Rocket'},
  returngo: {signalKey: 'returngo', label: 'Returngo'},
  returnly: {signalKey: 'returnly', label: 'Returnly'},
  'reveal-js': {signalKey: 'revealJs', label: 'Reveal Js'},
  revenuehunt: {signalKey: 'revenuehunt', label: 'Revenuehunt'},
  'reviews-io': {signalKey: 'reviewsIo', label: 'Reviews Io'},
  revlifter: {signalKey: 'revlifter', label: 'Revlifter'},
  revy: {signalKey: 'revy', label: 'Revy'},
  'rich-plugins-reviews': {
    signalKey: 'richPluginsReviews',
    label: 'Rich Plugins Reviews',
  },
  richrelevance: {signalKey: 'richrelevance', label: 'Richrelevance'},
  riot: {signalKey: 'riot', label: 'Riot'},
  'rise-ai': {signalKey: 'riseAi', label: 'Rise Ai'},
  riskified: {signalKey: 'riskified', label: 'Riskified'},
  rive: {signalKey: 'rive', label: 'Rive'},
  rockerbox: {signalKey: 'rockerbox', label: 'Rockerbox'},
  'rocket-chat': {signalKey: 'rocketChat', label: 'Rocket Chat'},
  rockrms: {signalKey: 'rockrms', label: 'Rockrms'},
  roistat: {signalKey: 'roistat', label: 'Roistat'},
  rokt: {signalKey: 'rokt', label: 'Rokt'},
  rollbar: {signalKey: 'rollbar', label: 'Rollbar'},
  route: {signalKey: 'route', label: 'Route'},
  'royal-mail': {signalKey: 'royalMail', label: 'Royal Mail'},
  rss: {signalKey: 'rss', label: 'Rss'},
  'rtb-house': {signalKey: 'rtbHouse', label: 'Rtb House'},
  'rubicon-project': {signalKey: 'rubiconProject', label: 'Rubicon Project'},
  ruby: {signalKey: 'ruby', label: 'Ruby'},
  'ruby-on-rails': {signalKey: 'rubyOnRails', label: 'Ruby On Rails'},
  'ruby-receptionists': {
    signalKey: 'rubyReceptionists',
    label: 'Ruby Receptionists',
  },
  rxjs: {signalKey: 'rxjs', label: 'Rxjs'},
  ryviu: {signalKey: 'ryviu', label: 'Ryviu'},
  'sails-js': {signalKey: 'sailsJs', label: 'Sails Js'},
  'sakura-internet': {signalKey: 'sakuraInternet', label: 'Sakura Internet'},
  salecycle: {signalKey: 'salecycle', label: 'Salecycle'},
  salesfire: {signalKey: 'salesfire', label: 'Salesfire'},
  salesfloor: {signalKey: 'salesfloor', label: 'Salesfloor'},
  'salesforce-audience-studio': {
    signalKey: 'salesforceAudienceStudio',
    label: 'Salesforce Audience Studio',
  },
  'salesforce-commerce-cloud': {
    signalKey: 'salesforceCommerceCloud',
    label: 'Salesforce Commerce Cloud',
  },
  'salesforce-interaction-studio': {
    signalKey: 'salesforceInteractionStudio',
    label: 'Salesforce Interaction Studio',
  },
  salesmanago: {signalKey: 'salesmanago', label: 'Salesmanago'},
  salla: {signalKey: 'salla', label: 'Salla'},
  salsify: {signalKey: 'salsify', label: 'Salsify'},
  sanity: {signalKey: 'sanity', label: 'Sanity'},
  'sap-commerce-cloud': {
    signalKey: 'sapCommerceCloud',
    label: 'Sap Commerce Cloud',
  },
  'sap-customer-data-cloud-sign-in': {
    signalKey: 'sapCustomerDataCloudSignIn',
    label: 'Sap Customer Data Cloud Sign In',
  },
  sapper: {signalKey: 'sapper', label: 'Sapper'},
  sass: {signalKey: 'sass', label: 'Sass'},
  scala: {signalKey: 'scala', label: 'Scala'},
  scalapay: {signalKey: 'scalapay', label: 'Scalapay'},
  'schedule-engine': {signalKey: 'scheduleEngine', label: 'Schedule Engine'},
  'scoop-it': {signalKey: 'scoopIt', label: 'Scoop It'},
  scorpion: {signalKey: 'scorpion', label: 'Scorpion'},
  'script-aculo-us': {signalKey: 'scriptAculoUs', label: 'Script Aculo Us'},
  scrollmagic: {signalKey: 'scrollmagic', label: 'Scrollmagic'},
  scrollreveal: {signalKey: 'scrollreveal', label: 'Scrollreveal'},
  scully: {signalKey: 'scully', label: 'Scully'},
  'sdl-tridion': {signalKey: 'sdlTridion', label: 'Sdl Tridion'},
  'seal-subscriptions': {
    signalKey: 'sealSubscriptions',
    label: 'Seal Subscriptions',
  },
  searchanise: {signalKey: 'searchanise', label: 'Searchanise'},
  searchspring: {signalKey: 'searchspring', label: 'Searchspring'},
  secomapp: {signalKey: 'secomapp', label: 'Secomapp'},
  sectigo: {signalKey: 'sectigo', label: 'Sectigo'},
  'section-io': {signalKey: 'sectionIo', label: 'Section Io'},
  'sections-design-shopify-app-optimization': {
    signalKey: 'sectionsDesignShopifyAppOptimization',
    label: 'Sections Design Shopify App Optimization',
  },
  segmanta: {signalKey: 'segmanta', label: 'Segmanta'},
  'segment-consent-manager': {
    signalKey: 'segmentConsentManager',
    label: 'Segment Consent Manager',
  },
  select2: {signalKey: 'select2', label: 'Select2'},
  selectize: {signalKey: 'selectize', label: 'Selectize'},
  'semantic-ui': {signalKey: 'semanticUi', label: 'Semantic Ui'},
  'sematext-experience': {
    signalKey: 'sematextExperience',
    label: 'Sematext Experience',
  },
  semplice: {signalKey: 'semplice', label: 'Semplice'},
  sendinblue: {signalKey: 'sendinblue', label: 'Sendinblue'},
  sendpulse: {signalKey: 'sendpulse', label: 'Sendpulse'},
  'sensors-data': {signalKey: 'sensorsData', label: 'Sensors Data'},
  seomatic: {signalKey: 'seomatic', label: 'Seomatic'},
  seravo: {signalKey: 'seravo', label: 'Seravo'},
  'service-management-group': {
    signalKey: 'serviceManagementGroup',
    label: 'Service Management Group',
  },
  sezzle: {signalKey: 'sezzle', label: 'Sezzle'},
  'shadcn-ui': {signalKey: 'shadcnUi', label: 'Shadcn Ui'},
  'shaka-player': {signalKey: 'shakaPlayer', label: 'Shaka Player'},
  shareaholic: {signalKey: 'shareaholic', label: 'Shareaholic'},
  sharethis: {signalKey: 'sharethis', label: 'Sharethis'},
  sharethrough: {signalKey: 'sharethrough', label: 'Sharethrough'},
  'she-media': {signalKey: 'sheMedia', label: 'She Media'},
  sheerid: {signalKey: 'sheerid', label: 'Sheerid'},
  'shine-js': {signalKey: 'shineJs', label: 'Shine Js'},
  'shogun-frontend': {signalKey: 'shogunFrontend', label: 'Shogun Frontend'},
  'shogun-landing-page-builder': {
    signalKey: 'shogunLandingPageBuilder',
    label: 'Shogun Landing Page Builder',
  },
  'shogun-page-builder': {
    signalKey: 'shogunPageBuilder',
    label: 'Shogun Page Builder',
  },
  'shop-pay': {signalKey: 'shopPay', label: 'Shop Pay'},
  shopapps: {signalKey: 'shopapps', label: 'Shopapps'},
  'shopify-buy-button': {
    signalKey: 'shopifyBuyButton',
    label: 'Shopify Buy Button',
  },
  'shopify-chat': {signalKey: 'shopifyChat', label: 'Shopify Chat'},
  'shopify-product-reviews': {
    signalKey: 'shopifyProductReviews',
    label: 'Shopify Product Reviews',
  },
  shoplazza: {signalKey: 'shoplazza', label: 'Shoplazza'},
  'shoppad-infinite-options': {
    signalKey: 'shoppadInfiniteOptions',
    label: 'Shoppad Infinite Options',
  },
  shoppingfeeder: {signalKey: 'shoppingfeeder', label: 'Shoppingfeeder'},
  shoppinggives: {signalKey: 'shoppinggives', label: 'Shoppinggives'},
  shoprunner: {signalKey: 'shoprunner', label: 'Shoprunner'},
  'shortcodes-ultimate': {
    signalKey: 'shortcodesUltimate',
    label: 'Shortcodes Ultimate',
  },
  'sidearm-sports': {signalKey: 'sidearmSports', label: 'Sidearm Sports'},
  sift: {signalKey: 'sift', label: 'Sift'},
  signal: {signalKey: 'signal', label: 'Signal'},
  signifyd: {signalKey: 'signifyd', label: 'Signifyd'},
  silverstripe: {signalKey: 'silverstripe', label: 'Silverstripe'},
  simon: {signalKey: 'simon', label: 'Simon'},
  'simple-analytics': {signalKey: 'simpleAnalytics', label: 'Simple Analytics'},
  simplehttp: {signalKey: 'simplehttp', label: 'Simplehttp'},
  simplero: {signalKey: 'simplero', label: 'Simplero'},
  'simplero-websites': {
    signalKey: 'simpleroWebsites',
    label: 'Simplero Websites',
  },
  simplesamlphp: {signalKey: 'simplesamlphp', label: 'Simplesamlphp'},
  sinatra: {signalKey: 'sinatra', label: 'Sinatra'},
  'site-kit': {signalKey: 'siteKit', label: 'Site Kit'},
  'site-search-360': {signalKey: 'siteSearch360', label: 'Site Search 360'},
  site24x7: {signalKey: 'site24x7', label: 'Site24x7'},
  'sitecore-engagement-cloud': {
    signalKey: 'sitecoreEngagementCloud',
    label: 'Sitecore Engagement Cloud',
  },
  siteground: {signalKey: 'siteground', label: 'Siteground'},
  'siteorigin-page-builder': {
    signalKey: 'siteoriginPageBuilder',
    label: 'Siteorigin Page Builder',
  },
  'siteorigin-vantage': {
    signalKey: 'siteoriginVantage',
    label: 'Siteorigin Vantage',
  },
  'siteorigin-widgets-bundle': {
    signalKey: 'siteoriginWidgetsBundle',
    label: 'Siteorigin Widgets Bundle',
  },
  sitespect: {signalKey: 'sitespect', label: 'Sitespect'},
  'sitevision-cms': {signalKey: 'sitevisionCms', label: 'Sitevision Cms'},
  sizmek: {signalKey: 'sizmek', label: 'Sizmek'},
  skai: {signalKey: 'skai', label: 'Skai'},
  skimlinks: {signalKey: 'skimlinks', label: 'Skimlinks'},
  skio: {signalKey: 'skio', label: 'Skio'},
  skyverge: {signalKey: 'skyverge', label: 'Skyverge'},
  sleeknote: {signalKey: 'sleeknote', label: 'Sleeknote'},
  slick: {signalKey: 'slick', label: 'Slick'},
  'slider-revolution': {
    signalKey: 'sliderRevolution',
    label: 'Slider Revolution',
  },
  slimbox: {signalKey: 'slimbox', label: 'Slimbox'},
  'slimbox-2': {signalKey: 'slimbox2', label: 'Slimbox 2'},
  'smart-ad-server': {signalKey: 'smartAdServer', label: 'Smart Ad Server'},
  'smart-slider-3': {signalKey: 'smartSlider3', label: 'Smart Slider 3'},
  'smarter-click': {signalKey: 'smarterClick', label: 'Smarter Click'},
  smartling: {signalKey: 'smartling', label: 'Smartling'},
  smartlook: {signalKey: 'smartlook', label: 'Smartlook'},
  smartrecruiters: {
    signalKey: 'wappSmartrecruiters',
    label: 'SmartRecruiters (Web)',
  },
  smartsupp: {signalKey: 'smartsupp', label: 'Smartsupp'},
  'smash-balloon-instagram-feed': {
    signalKey: 'smashBalloonInstagramFeed',
    label: 'Smash Balloon Instagram Feed',
  },
  smile: {signalKey: 'smile', label: 'Smile'},
  'smile-app': {signalKey: 'smileApp', label: 'Smile App'},
  smtpjs: {signalKey: 'smtpjs', label: 'Smtpjs'},
  'snap-pixel': {signalKey: 'snapPixel', label: 'Snap Pixel'},
  'snap-svg': {signalKey: 'snapSvg', label: 'Snap Svg'},
  snapengage: {signalKey: 'snapengage', label: 'Snapengage'},
  snapwidget: {signalKey: 'snapwidget', label: 'Snapwidget'},
  snipcart: {signalKey: 'snipcart', label: 'Snipcart'},
  sniply: {signalKey: 'sniply', label: 'Sniply'},
  social9: {signalKey: 'social9', label: 'Social9'},
  socialladder: {signalKey: 'socialladder', label: 'Socialladder'},
  'socket-io': {signalKey: 'socketIo', label: 'Socket Io'},
  softr: {signalKey: 'softr', label: 'Softr'},
  solidjs: {signalKey: 'solidjs', label: 'Solidjs'},
  solodev: {signalKey: 'solodev', label: 'Solodev'},
  solvvy: {signalKey: 'solvvy', label: 'Solvvy'},
  sonobi: {signalKey: 'sonobi', label: 'Sonobi'},
  soundcloud: {signalKey: 'soundcloud', label: 'Soundcloud'},
  soundmanager: {signalKey: 'soundmanager', label: 'Soundmanager'},
  sourcepoint: {signalKey: 'sourcepoint', label: 'Sourcepoint'},
  sovrn: {signalKey: 'sovrn', label: 'Sovrn'},
  'sovrn-commerce': {signalKey: 'sovrnCommerce', label: 'Sovrn Commerce'},
  'spatie-laravel-cookie-consent': {
    signalKey: 'spatieLaravelCookieConsent',
    label: 'Spatie Laravel Cookie Consent',
  },
  spectra: {signalKey: 'spectra', label: 'Spectra'},
  speedcurve: {signalKey: 'speedcurve', label: 'Speedcurve'},
  sphinx: {signalKey: 'sphinx', label: 'Sphinx'},
  splide: {signalKey: 'splide', label: 'Splide'},
  split: {signalKey: 'split', label: 'Split'},
  splitbee: {signalKey: 'splitbee', label: 'Splitbee'},
  splitit: {signalKey: 'splitit', label: 'Splitit'},
  'spotify-web-api': {signalKey: 'spotifyWebApi', label: 'Spotify Web Api'},
  'spotify-widgets': {signalKey: 'spotifyWidgets', label: 'Spotify Widgets'},
  spotx: {signalKey: 'spotx', label: 'Spotx'},
  'sprig-plugin': {signalKey: 'sprigPlugin', label: 'Sprig Plugin'},
  spring: {signalKey: 'spring', label: 'Spring'},
  spritespin: {signalKey: 'spritespin', label: 'Spritespin'},
  spurit: {signalKey: 'spurit', label: 'Spurit'},
  square: {signalKey: 'square', label: 'Square'},
  'square-online': {signalKey: 'squareOnline', label: 'Square Online'},
  squeezely: {signalKey: 'squeezely', label: 'Squeezely'},
  stackable: {signalKey: 'stackable', label: 'Stackable'},
  stackbit: {signalKey: 'stackbit', label: 'Stackbit'},
  stackpath: {signalKey: 'stackpath', label: 'Stackpath'},
  stamped: {signalKey: 'stamped', label: 'Stamped'},
  statamic: {signalKey: 'statamic', label: 'Statamic'},
  statcounter: {signalKey: 'statcounter', label: 'Statcounter'},
  statically: {signalKey: 'statically', label: 'Statically'},
  statsig: {signalKey: 'wappStatsig', label: 'Statsig (Web)'},
  statuspal: {signalKey: 'statuspal', label: 'Statuspal'},
  steelhouse: {signalKey: 'steelhouse', label: 'Steelhouse'},
  stencil: {signalKey: 'stencil', label: 'Stencil'},
  stimulus: {signalKey: 'stimulus', label: 'Stimulus'},
  stitches: {signalKey: 'stitches', label: 'Stitches'},
  'stn-video': {signalKey: 'stnVideo', label: 'Stn Video'},
  storyblok: {signalKey: 'storyblok', label: 'Storyblok'},
  storybook: {signalKey: 'storybook', label: 'Storybook'},
  strapi: {signalKey: 'strapi', label: 'Strapi'},
  strato: {signalKey: 'strato', label: 'Strato'},
  strattic: {signalKey: 'strattic', label: 'Strattic'},
  strikingly: {signalKey: 'strikingly', label: 'Strikingly'},
  'styled-components': {
    signalKey: 'styledComponents',
    label: 'Styled Components',
  },
  stylitics: {signalKey: 'stylitics', label: 'Stylitics'},
  substack: {signalKey: 'substack', label: 'Substack'},
  sucuri: {signalKey: 'sucuri', label: 'Sucuri'},
  sulu: {signalKey: 'sulu', label: 'Sulu'},
  summernote: {signalKey: 'summernote', label: 'Summernote'},
  sumo: {signalKey: 'sumo', label: 'Sumo'},
  supabase: {signalKey: 'supabase', label: 'Supabase'},
  'super-builder': {signalKey: 'superBuilder', label: 'Super Builder'},
  'super-socializer': {signalKey: 'superSocializer', label: 'Super Socializer'},
  superpwa: {signalKey: 'superpwa', label: 'Superpwa'},
  supersized: {signalKey: 'supersized', label: 'Supersized'},
  survicate: {signalKey: 'survicate', label: 'Survicate'},
  suse: {signalKey: 'suse', label: 'Suse'},
  svelte: {signalKey: 'svelte', label: 'Svelte'},
  sveltekit: {signalKey: 'sveltekit', label: 'Sveltekit'},
  'svg-support': {signalKey: 'svgSupport', label: 'Svg Support'},
  'swagger-ui': {signalKey: 'swaggerUi', label: 'Swagger Ui'},
  swc: {signalKey: 'swc', label: 'Swc'},
  sweetalert: {signalKey: 'sweetalert', label: 'Sweetalert'},
  sweetalert2: {signalKey: 'sweetalert2', label: 'Sweetalert2'},
  swfobject: {signalKey: 'swfobject', label: 'Swfobject'},
  'swiffy-slider': {signalKey: 'swiffySlider', label: 'Swiffy Slider'},
  swiftype: {signalKey: 'swiftype', label: 'Swiftype'},
  swiper: {signalKey: 'swiper', label: 'Swiper'},
  'swym-wishlist-plus': {
    signalKey: 'swymWishlistPlus',
    label: 'Swym Wishlist Plus',
  },
  symfony: {signalKey: 'symfony', label: 'Symfony'},
  syndeca: {signalKey: 'syndeca', label: 'Syndeca'},
  syntaxhighlighter: {
    signalKey: 'syntaxhighlighter',
    label: 'Syntaxhighlighter',
  },
  syte: {signalKey: 'syte', label: 'Syte'},
  tabarnapp: {signalKey: 'tabarnapp', label: 'Tabarnapp'},
  tabby: {signalKey: 'tabby', label: 'Tabby'},
  tablepress: {signalKey: 'tablepress', label: 'Tablepress'},
  taboola: {signalKey: 'taboola', label: 'Taboola'},
  tachyons: {signalKey: 'tachyons', label: 'Tachyons'},
  tagembed: {signalKey: 'tagembed', label: 'Tagembed'},
  taggbox: {signalKey: 'taggbox', label: 'Taggbox'},
  tagpro: {signalKey: 'tagpro', label: 'Tagpro'},
  'tailwind-css': {signalKey: 'tailwindCss', label: 'Tailwind Css'},
  talkable: {signalKey: 'talkable', label: 'Talkable'},
  tally: {signalKey: 'tally', label: 'Tally'},
  'tangled-network': {signalKey: 'tangledNetwork', label: 'Tangled Network'},
  tapad: {signalKey: 'tapad', label: 'Tapad'},
  tapcart: {signalKey: 'tapcart', label: 'Tapcart'},
  tatari: {signalKey: 'tatari', label: 'Tatari'},
  'tawk-to': {signalKey: 'tawkTo', label: 'Tawk To'},
  tdesign: {signalKey: 'tdesign', label: 'Tdesign'},
  teachable: {signalKey: 'teachable', label: 'Teachable'},
  teads: {signalKey: 'teads', label: 'Teads'},
  'tealium-audiencestream': {
    signalKey: 'tealiumAudiencestream',
    label: 'Tealium Audiencestream',
  },
  'tealium-consent-management': {
    signalKey: 'tealiumConsentManagement',
    label: 'Tealium Consent Management',
  },
  teamtailor: {signalKey: 'wappTeamtailor', label: 'Teamtailor (Web)'},
  'tencent-waterproof-wall': {
    signalKey: 'tencentWaterproofWall',
    label: 'Tencent Waterproof Wall',
  },
  tengine: {signalKey: 'tengine', label: 'Tengine'},
  termly: {signalKey: 'termly', label: 'Termly'},
  tessitura: {signalKey: 'tessitura', label: 'Tessitura'},
  'the-arena-group': {signalKey: 'theArenaGroup', label: 'The Arena Group'},
  'the-events-calendar': {
    signalKey: 'theEventsCalendar',
    label: 'The Events Calendar',
  },
  'the-hotels-network': {
    signalKey: 'theHotelsNetwork',
    label: 'The Hotels Network',
  },
  'the-seo-framework': {
    signalKey: 'theSeoFramework',
    label: 'The Seo Framework',
  },
  'theatre-js': {signalKey: 'theatreJs', label: 'Theatre Js'},
  'theme-freesia-edge': {
    signalKey: 'themeFreesiaEdge',
    label: 'Theme Freesia Edge',
  },
  'theme-freesia-photograph': {
    signalKey: 'themeFreesiaPhotograph',
    label: 'Theme Freesia Photograph',
  },
  'theme-vision-agama': {
    signalKey: 'themeVisionAgama',
    label: 'Theme Vision Agama',
  },
  'theme4press-evolve': {
    signalKey: 'theme4pressEvolve',
    label: 'Theme4press Evolve',
  },
  'themegrill-flash': {signalKey: 'themegrillFlash', label: 'Themegrill Flash'},
  'themegrill-spacious': {
    signalKey: 'themegrillSpacious',
    label: 'Themegrill Spacious',
  },
  'themeisle-menu-icons': {
    signalKey: 'themeisleMenuIcons',
    label: 'Themeisle Menu Icons',
  },
  'themezee-poseidon': {
    signalKey: 'themezeePoseidon',
    label: 'Themezee Poseidon',
  },
  'themonic-iconic-one': {
    signalKey: 'themonicIconicOne',
    label: 'Themonic Iconic One',
  },
  thesis: {signalKey: 'thesis', label: 'Thesis'},
  'thimpress-learnpress': {
    signalKey: 'thimpressLearnpress',
    label: 'Thimpress Learnpress',
  },
  'think-up-themes-consulting': {
    signalKey: 'thinkUpThemesConsulting',
    label: 'Think Up Themes Consulting',
  },
  thinkific: {signalKey: 'thinkific', label: 'Thinkific'},
  thinkphp: {signalKey: 'thinkphp', label: 'Thinkphp'},
  threatmetrix: {signalKey: 'threatmetrix', label: 'Threatmetrix'},
  'three-js': {signalKey: 'threeJs', label: 'Three Js'},
  threekit: {signalKey: 'threekit', label: 'Threekit'},
  'thrive-apprentice': {
    signalKey: 'thriveApprentice',
    label: 'Thrive Apprentice',
  },
  'thrive-architect': {signalKey: 'thriveArchitect', label: 'Thrive Architect'},
  'thrive-comments': {signalKey: 'thriveComments', label: 'Thrive Comments'},
  'thrive-leads': {signalKey: 'thriveLeads', label: 'Thrive Leads'},
  'thrive-quiz-builder': {
    signalKey: 'thriveQuizBuilder',
    label: 'Thrive Quiz Builder',
  },
  'thrive-ultimatum': {signalKey: 'thriveUltimatum', label: 'Thrive Ultimatum'},
  thrivecart: {signalKey: 'thrivecart', label: 'Thrivecart'},
  tidio: {signalKey: 'tidio', label: 'Tidio'},
  tilda: {signalKey: 'tilda', label: 'Tilda'},
  tiledesk: {signalKey: 'tiledesk', label: 'Tiledesk'},
  'tiny-slider': {signalKey: 'tinySlider', label: 'Tiny Slider'},
  tinybird: {signalKey: 'tinybird', label: 'Tinybird'},
  tinymce: {signalKey: 'tinymce', label: 'Tinymce'},
  'tippy-js': {signalKey: 'tippyJs', label: 'Tippy Js'},
  tipsa: {signalKey: 'tipsa', label: 'Tipsa'},
  tiqets: {signalKey: 'tiqets', label: 'Tiqets'},
  'tn-express-web': {signalKey: 'tnExpressWeb', label: 'Tn Express Web'},
  toastr: {signalKey: 'toastr', label: 'Toastr'},
  tolt: {signalKey: 'tolt', label: 'Tolt'},
  'tomtom-maps': {signalKey: 'tomtomMaps', label: 'Tomtom Maps'},
  tornadoserver: {signalKey: 'tornadoserver', label: 'Tornadoserver'},
  'total-js': {signalKey: 'totalJs', label: 'Total Js'},
  townnews: {signalKey: 'townnews', label: 'Townnews'},
  trackjs: {signalKey: 'trackjs', label: 'Trackjs'},
  tradedoubler: {signalKey: 'tradedoubler', label: 'Tradedoubler'},
  tradingview: {signalKey: 'tradingview', label: 'Tradingview'},
  transifex: {signalKey: 'transifex', label: 'Transifex'},
  'transistor-fm': {signalKey: 'transistorFm', label: 'Transistor Fm'},
  'translate-wordpress': {
    signalKey: 'translateWordpress',
    label: 'Translate Wordpress',
  },
  'treasure-data': {signalKey: 'treasureData', label: 'Treasure Data'},
  trengo: {signalKey: 'trengo', label: 'Trengo'},
  'trinity-audio': {signalKey: 'trinityAudio', label: 'Trinity Audio'},
  'triple-whale': {signalKey: 'tripleWhale', label: 'Triple Whale'},
  triplelift: {signalKey: 'triplelift', label: 'Triplelift'},
  trix: {signalKey: 'trix', label: 'Trix'},
  'true-fit': {signalKey: 'trueFit', label: 'True Fit'},
  truendo: {signalKey: 'truendo', label: 'Truendo'},
  trumba: {signalKey: 'trumba', label: 'Trumba'},
  trustarc: {signalKey: 'trustarc', label: 'Trustarc'},
  'trusted-shops': {signalKey: 'trustedShops', label: 'Trusted Shops'},
  trustindex: {signalKey: 'trustindex', label: 'Trustindex'},
  trustvox: {signalKey: 'trustvox', label: 'Trustvox'},
  truvalidate: {signalKey: 'truvalidate', label: 'Truvalidate'},
  turbo: {signalKey: 'turbo', label: 'Turbo'},
  turbolinks: {signalKey: 'turbolinks', label: 'Turbolinks'},
  turfjs: {signalKey: 'turfjs', label: 'Turfjs'},
  tvsquared: {signalKey: 'tvsquared', label: 'Tvsquared'},
  'twenty-eleven': {signalKey: 'twentyEleven', label: 'Twenty Eleven'},
  'twenty-fifteen': {signalKey: 'twentyFifteen', label: 'Twenty Fifteen'},
  'twenty-fourteen': {signalKey: 'twentyFourteen', label: 'Twenty Fourteen'},
  'twenty-nineteen': {signalKey: 'twentyNineteen', label: 'Twenty Nineteen'},
  'twenty-seventeen': {signalKey: 'twentySeventeen', label: 'Twenty Seventeen'},
  'twenty-sixteen': {signalKey: 'twentySixteen', label: 'Twenty Sixteen'},
  'twenty-ten': {signalKey: 'twentyTen', label: 'Twenty Ten'},
  'twenty-thirteen': {signalKey: 'twentyThirteen', label: 'Twenty Thirteen'},
  'twenty-twelve': {signalKey: 'twentyTwelve', label: 'Twenty Twelve'},
  'twenty-twenty': {signalKey: 'twentyTwenty', label: 'Twenty Twenty'},
  'twenty-twenty-one': {
    signalKey: 'twentyTwentyOne',
    label: 'Twenty Twenty One',
  },
  'twenty-twenty-three': {
    signalKey: 'twentyTwentyThree',
    label: 'Twenty Twenty Three',
  },
  'twenty-twenty-two': {
    signalKey: 'twentyTwentyTwo',
    label: 'Twenty Twenty Two',
  },
  twicpics: {signalKey: 'twicpics', label: 'Twicpics'},
  twik: {signalKey: 'twik', label: 'Twik'},
  'twilio-authy': {signalKey: 'twilioAuthy', label: 'Twilio Authy'},
  'twitch-player': {signalKey: 'twitchPlayer', label: 'Twitch Player'},
  twitter: {signalKey: 'wappTwitter', label: 'Twitter (Web)'},
  'twitter-analytics': {
    signalKey: 'twitterAnalytics',
    label: 'Twitter Analytics',
  },
  'twitter-emoji-twemoji': {
    signalKey: 'twitterEmojiTwemoji',
    label: 'Twitter Emoji Twemoji',
  },
  'twitter-flight': {signalKey: 'twitterFlight', label: 'Twitter Flight'},
  'twitter-typeahead-js': {
    signalKey: 'twitterTypeaheadJs',
    label: 'Twitter Typeahead Js',
  },
  typekit: {signalKey: 'typekit', label: 'Typekit'},
  typepad: {signalKey: 'typepad', label: 'Typepad'},
  'typer-js': {signalKey: 'typerJs', label: 'Typer Js'},
  typescript: {signalKey: 'wappTypescript', label: 'Typescript (Web)'},
  'typo3-cms': {signalKey: 'typo3Cms', label: 'Typo3 Cms'},
  ubuntu: {signalKey: 'ubuntu', label: 'Ubuntu'},
  uikit: {signalKey: 'uikit', label: 'Uikit'},
  ukfast: {signalKey: 'ukfast', label: 'Ukfast'},
  'ultimate-addons-for-elementor': {
    signalKey: 'ultimateAddonsForElementor',
    label: 'Ultimate Addons For Elementor',
  },
  'ultimate-gdpr-ccpa': {
    signalKey: 'ultimateGdprCcpa',
    label: 'Ultimate Gdpr Ccpa',
  },
  ultimatelysocial: {signalKey: 'ultimatelysocial', label: 'Ultimatelysocial'},
  umami: {signalKey: 'umami', label: 'Umami'},
  umbraco: {signalKey: 'umbraco', label: 'Umbraco'},
  umijs: {signalKey: 'umijs', label: 'Umijs'},
  umso: {signalKey: 'umso', label: 'Umso'},
  unbxd: {signalKey: 'unbxd', label: 'Unbxd'},
  'underscore-js': {signalKey: 'underscoreJs', label: 'Underscore Js'},
  understrap: {signalKey: 'understrap', label: 'Understrap'},
  uniconsent: {signalKey: 'uniconsent', label: 'Uniconsent'},
  'unicorn-platform': {signalKey: 'unicornPlatform', label: 'Unicorn Platform'},
  unix: {signalKey: 'unix', label: 'Unix'},
  unocss: {signalKey: 'unocss', label: 'Unocss'},
  unruly: {signalKey: 'unruly', label: 'Unruly'},
  upfluence: {signalKey: 'upfluence', label: 'Upfluence'},
  uploadcare: {signalKey: 'uploadcare', label: 'Uploadcare'},
  ups: {signalKey: 'ups', label: 'Ups'},
  upsellit: {signalKey: 'upsellit', label: 'Upsellit'},
  upserve: {signalKey: 'upserve', label: 'Upserve'},
  uptimerobot: {signalKey: 'uptimerobot', label: 'Uptimerobot'},
  uptrends: {signalKey: 'uptrends', label: 'Uptrends'},
  upvoty: {signalKey: 'upvoty', label: 'Upvoty'},
  uremediate: {signalKey: 'uremediate', label: 'Uremediate'},
  usablenet: {signalKey: 'usablenet', label: 'Usablenet'},
  uscreen: {signalKey: 'uscreen', label: 'Uscreen'},
  usercentrics: {signalKey: 'usercentrics', label: 'Usercentrics'},
  userflow: {signalKey: 'userflow', label: 'Userflow'},
  userlike: {signalKey: 'userlike', label: 'Userlike'},
  userpilot: {signalKey: 'userpilot', label: 'Userpilot'},
  userreport: {signalKey: 'userreport', label: 'Userreport'},
  uservoice: {signalKey: 'uservoice', label: 'Uservoice'},
  userway: {signalKey: 'userway', label: 'Userway'},
  usps: {signalKey: 'usps', label: 'Usps'},
  uvicorn: {signalKey: 'uvicorn', label: 'Uvicorn'},
  'vanco-payment-solutions': {
    signalKey: 'vancoPaymentSolutions',
    label: 'Vanco Payment Solutions',
  },
  variti: {signalKey: 'variti', label: 'Variti'},
  varnish: {signalKey: 'varnish', label: 'Varnish'},
  vbulletin: {signalKey: 'vbulletin', label: 'Vbulletin'},
  vcita: {signalKey: 'vcita', label: 'Vcita'},
  'vdx-tv': {signalKey: 'vdxTv', label: 'Vdx Tv'},
  vectary: {signalKey: 'vectary', label: 'Vectary'},
  venmo: {signalKey: 'venmo', label: 'Venmo'},
  ventraip: {signalKey: 'ventraip', label: 'Ventraip'},
  'vercel-analytics': {signalKey: 'vercelAnalytics', label: 'Vercel Analytics'},
  'verifone-2checkout': {
    signalKey: 'verifone2checkout',
    label: 'Verifone 2checkout',
  },
  verifypass: {signalKey: 'verifypass', label: 'Verifypass'},
  'verizon-media': {signalKey: 'verizonMedia', label: 'Verizon Media'},
  'very-good-security': {
    signalKey: 'veryGoodSecurity',
    label: 'Very Good Security',
  },
  vev: {signalKey: 'vev', label: 'Vev'},
  viafoura: {signalKey: 'viafoura', label: 'Viafoura'},
  vidazoo: {signalKey: 'vidazoo', label: 'Vidazoo'},
  videojs: {signalKey: 'videojs', label: 'Videojs'},
  vignette: {signalKey: 'vignette', label: 'Vignette'},
  'viral-loops': {signalKey: 'viralLoops', label: 'Viral Loops'},
  virtualspirits: {signalKey: 'virtualspirits', label: 'Virtualspirits'},
  virtuous: {signalKey: 'virtuous', label: 'Virtuous'},
  visa: {signalKey: 'visa', label: 'Visa'},
  'visa-checkout': {signalKey: 'visaCheckout', label: 'Visa Checkout'},
  'visual-composer': {signalKey: 'visualComposer', label: 'Visual Composer'},
  'visual-portfolio': {signalKey: 'visualPortfolio', label: 'Visual Portfolio'},
  visx: {signalKey: 'visx', label: 'Visx'},
  vite: {signalKey: 'vite', label: 'Vite'},
  vitepress: {signalKey: 'vitepress', label: 'Vitepress'},
  vizury: {signalKey: 'vizury', label: 'Vizury'},
  'vk-pixel': {signalKey: 'vkPixel', label: 'Vk Pixel'},
  'vonage-video-api': {signalKey: 'vonageVideoApi', label: 'Vonage Video Api'},
  'voog-com-website-builder': {
    signalKey: 'voogComWebsiteBuilder',
    label: 'Voog Com Website Builder',
  },
  'vp-asp': {signalKey: 'vpAsp', label: 'Vp Asp'},
  vtex: {signalKey: 'vtex', label: 'Vtex'},
  'vue-storefront': {signalKey: 'vueStorefront', label: 'Vue Storefront'},
  vuepress: {signalKey: 'vuepress', label: 'Vuepress'},
  vuetify: {signalKey: 'vuetify', label: 'Vuetify'},
  vuex: {signalKey: 'vuex', label: 'Vuex'},
  'vwo-engage': {signalKey: 'vwoEngageWeb', label: 'VWO Engage (Web)'},
  'w3-css': {signalKey: 'w3Css', label: 'W3 Css'},
  'w3-total-cache': {signalKey: 'w3TotalCache', label: 'W3 Total Cache'},
  w3counter: {signalKey: 'w3counter', label: 'W3counter'},
  wagtail: {signalKey: 'wagtail', label: 'Wagtail'},
  waitlist: {signalKey: 'waitlist', label: 'Waitlist'},
  wazimo: {signalKey: 'wazimo', label: 'Wazimo'},
  'web-shop-manager': {signalKey: 'webShopManager', label: 'Web Shop Manager'},
  'web-vitals': {signalKey: 'webVitals', label: 'Web Vitals'},
  webassembly: {signalKey: 'webassembly', label: 'Webassembly'},
  'webfactory-maintenance': {
    signalKey: 'webfactoryMaintenance',
    label: 'Webfactory Maintenance',
  },
  webgains: {signalKey: 'webgains', label: 'Webgains'},
  weblium: {signalKey: 'weblium', label: 'Weblium'},
  webpack: {signalKey: 'webpack', label: 'Webpack'},
  webpushr: {signalKey: 'webpushr', label: 'Webpushr'},
  webrick: {signalKey: 'webrick', label: 'Webrick'},
  'webriti-busiprof': {signalKey: 'webritiBusiprof', label: 'Webriti Busiprof'},
  websitebuilder: {signalKey: 'websitebuilder', label: 'Websitebuilder'},
  webtrends: {signalKey: 'webtrends', label: 'Webtrends'},
  weebly: {signalKey: 'weebly', label: 'Weebly'},
  weglot: {signalKey: 'weglot', label: 'Weglot'},
  'weltpixel-pearl-theme': {
    signalKey: 'weltpixelPearlTheme',
    label: 'Weltpixel Pearl Theme',
  },
  'whatsapp-business-chat': {
    signalKey: 'whatsappBusinessChat',
    label: 'Whatsapp Business Chat',
  },
  wheelio: {signalKey: 'wheelio', label: 'Wheelio'},
  wigzo: {signalKey: 'wigzo', label: 'Wigzo'},
  wildjar: {signalKey: 'wildjar', label: 'Wildjar'},
  'windows-server': {signalKey: 'windowsServer', label: 'Windows Server'},
  wink: {signalKey: 'wink', label: 'Wink'},
  wisepops: {signalKey: 'wisepops', label: 'Wisepops'},
  'wishlist-king': {signalKey: 'wishlistKing', label: 'Wishlist King'},
  'with-reach': {signalKey: 'withReach', label: 'With Reach'},
  'wix-ecommerce': {signalKey: 'wixEcommerce', label: 'Wix Ecommerce'},
  'woocommerce-blocks': {
    signalKey: 'woocommerceBlocks',
    label: 'Woocommerce Blocks',
  },
  'woocommerce-multilingual': {
    signalKey: 'woocommerceMultilingual',
    label: 'Woocommerce Multilingual',
  },
  'woocommerce-paypal-checkout-payment-gateway': {
    signalKey: 'woocommercePaypalCheckoutPaymentGateway',
    label: 'Woocommerce Paypal Checkout Payment Gateway',
  },
  'woocommerce-paypal-payments': {
    signalKey: 'woocommercePaypalPayments',
    label: 'Woocommerce Paypal Payments',
  },
  'woocommerce-stripe-payment-gateway': {
    signalKey: 'woocommerceStripePaymentGateway',
    label: 'Woocommerce Stripe Payment Gateway',
  },
  'woocommerce-subscriptions': {
    signalKey: 'woocommerceSubscriptions',
    label: 'Woocommerce Subscriptions',
  },
  woopra: {signalKey: 'woopra', label: 'Woopra'},
  wordfence: {signalKey: 'wordfence', label: 'Wordfence'},
  'wordfence-login-security': {
    signalKey: 'wordfenceLoginSecurity',
    label: 'Wordfence Login Security',
  },
  'wordpress-com': {signalKey: 'wordpressCom', label: 'Wordpress Com'},
  'wordpress-multisite': {
    signalKey: 'wordpressMultisite',
    label: 'Wordpress Multisite',
  },
  'wordpress-super-cache': {
    signalKey: 'wordpressSuperCache',
    label: 'Wordpress Super Cache',
  },
  'wordpress-vip': {signalKey: 'wordpressVip', label: 'Wordpress Vip'},
  workable: {signalKey: 'wappWorkable', label: 'Workable (Web)'},
  worldpay: {signalKey: 'worldpay', label: 'Worldpay'},
  'wp-automatic': {signalKey: 'wpAutomatic', label: 'Wp Automatic'},
  'wp-fastest-cache': {signalKey: 'wpFastestCache', label: 'Wp Fastest Cache'},
  'wp-featherlight': {signalKey: 'wpFeatherlight', label: 'Wp Featherlight'},
  'wp-google-map-plugin': {
    signalKey: 'wpGoogleMapPlugin',
    label: 'Wp Google Map Plugin',
  },
  'wp-job-openings': {signalKey: 'wpJobOpenings', label: 'Wp Job Openings'},
  'wp-optimize': {signalKey: 'wpOptimize', label: 'Wp Optimize'},
  'wp-pagenavi': {signalKey: 'wpPagenavi', label: 'Wp Pagenavi'},
  'wp-rocket': {signalKey: 'wpRocket', label: 'Wp Rocket'},
  'wp-statistics': {signalKey: 'wpStatistics', label: 'Wp Statistics'},
  wpbakery: {signalKey: 'wpbakery', label: 'Wpbakery'},
  wpcache: {signalKey: 'wpcache', label: 'Wpcache'},
  wpforms: {signalKey: 'wpforms', label: 'Wpforms'},
  wpml: {signalKey: 'wpml', label: 'Wpml'},
  'wpmu-dev-smush': {signalKey: 'wpmuDevSmush', label: 'Wpmu Dev Smush'},
  wufoo: {signalKey: 'wufoo', label: 'Wufoo'},
  wunderkind: {signalKey: 'wunderkind', label: 'Wunderkind'},
  wurfl: {signalKey: 'wurfl', label: 'Wurfl'},
  'x-cart': {signalKey: 'xCart', label: 'X Cart'},
  xcharts: {signalKey: 'xcharts', label: 'Xcharts'},
  xregexp: {signalKey: 'xregexp', label: 'Xregexp'},
  xtime: {signalKey: 'xtime', label: 'Xtime'},
  xtra: {signalKey: 'xtra', label: 'Xtra'},
  xtremepush: {signalKey: 'xtremepush', label: 'Xtremepush'},
  'yahoo-ecommerce': {signalKey: 'yahooEcommerce', label: 'Yahoo Ecommerce'},
  'yandex-metrika': {signalKey: 'yandexMetrika', label: 'Yandex Metrika'},
  'yellow-ai': {signalKey: 'yellowAi', label: 'Yellow Ai'},
  'yelp-review-badge': {
    signalKey: 'yelpReviewBadge',
    label: 'Yelp Review Badge',
  },
  yett: {signalKey: 'yett', label: 'Yett'},
  yext: {signalKey: 'yext', label: 'Yext'},
  yieldify: {signalKey: 'yieldify', label: 'Yieldify'},
  yii: {signalKey: 'yii', label: 'Yii'},
  'yoast-duplicate-post': {
    signalKey: 'yoastDuplicatePost',
    label: 'Yoast Duplicate Post',
  },
  'yoast-seo': {signalKey: 'yoastSeo', label: 'Yoast Seo'},
  'yoast-seo-for-shopify': {
    signalKey: 'yoastSeoForShopify',
    label: 'Yoast Seo For Shopify',
  },
  'yoast-seo-premium': {
    signalKey: 'yoastSeoPremium',
    label: 'Yoast Seo Premium',
  },
  yola: {signalKey: 'yola', label: 'Yola'},
  'yotpo-loyalty-referrals': {
    signalKey: 'yotpoLoyaltyReferrals',
    label: 'Yotpo Loyalty Referrals',
  },
  'yotpo-smsbump': {signalKey: 'yotpoSmsbump', label: 'Yotpo Smsbump'},
  yottaa: {signalKey: 'yottaa', label: 'Yottaa'},
  'youcam-makeup': {signalKey: 'youcamMakeup', label: 'Youcam Makeup'},
  yui: {signalKey: 'yui', label: 'Yui'},
  'yui-doc': {signalKey: 'yuiDoc', label: 'Yui Doc'},
  zakeke: {signalKey: 'zakeke', label: 'Zakeke'},
  'zakeke-visual-customizer': {
    signalKey: 'zakekeVisualCustomizer',
    label: 'Zakeke Visual Customizer',
  },
  zakra: {signalKey: 'zakra', label: 'Zakra'},
  zend: {signalKey: 'zend', label: 'Zend'},
  'zendesk-chat': {signalKey: 'zendeskChat', label: 'Zendesk Chat'},
  'zendesk-sunshine-conversations': {
    signalKey: 'zendeskSunshineConversations',
    label: 'Zendesk Sunshine Conversations',
  },
  zeotap: {signalKey: 'zeotap', label: 'Zeotap'},
  zepto: {signalKey: 'zepto', label: 'Zepto'},
  ziggy: {signalKey: 'ziggy', label: 'Ziggy'},
  zinrelo: {signalKey: 'zinrelo', label: 'Zinrelo'},
  zip: {signalKey: 'zip', label: 'Zip'},
  'zipify-ocu': {signalKey: 'zipifyOcu', label: 'Zipify Ocu'},
  zipkin: {signalKey: 'zipkin', label: 'Zipkin'},
  'zmags-creator': {signalKey: 'zmagsCreator', label: 'Zmags Creator'},
  zocdoc: {signalKey: 'zocdoc', label: 'Zocdoc'},
  zoho: {signalKey: 'zoho', label: 'Zoho'},
  'zoho-pagesense': {signalKey: 'zohoPagesense', label: 'Zoho Pagesense'},
  'zone-js': {signalKey: 'zoneJs', label: 'Zone Js'},
  zonos: {signalKey: 'zonos', label: 'Zonos'},
  zotabox: {signalKey: 'zotabox', label: 'Zotabox'},
  'zurb-foundation': {signalKey: 'zurbFoundation', label: 'Zurb Foundation'},
} as const satisfies Record<string, WappSignalEntry>;

export type WappTech = keyof typeof WappStackSignals;

// --- Auto-generated signal labels ---
// Produces {signalKey: label} for entries without manualOverride,
// for spreading into ScoringSignalsDefinition.stackFit
export type AutoSignalEntry = Extract<WappSignalEntry, {signalKey: string}>;
export type AutoSignalSlug = {
  [K in WappTech]: (typeof WappStackSignals)[K] extends AutoSignalEntry
    ? K
    : never;
}[WappTech];

type AutoSignalLabels = {
  [K in AutoSignalSlug as (typeof WappStackSignals)[K] extends AutoSignalEntry
    ? (typeof WappStackSignals)[K]['signalKey']
    : never]: (typeof WappStackSignals)[K] extends AutoSignalEntry
    ? (typeof WappStackSignals)[K]['label']
    : never;
};

export const AutoEntries = entries(WappStackSignals).flatMap(([slug, entry]) =>
  entry !== null && 'signalKey' in entry
    ? [{slug, signalKey: entry.signalKey, label: entry.label}]
    : []
);

export const WappAutoSignalLabels = fromEntries(
  AutoEntries.map(({signalKey, label}) => [signalKey, label])
) as AutoSignalLabels;

export const WappAutoSignalKeys = new Set(
  AutoEntries.map(({signalKey}) => signalKey)
);

export const WappAutoSlugs = new Set(AutoEntries.map(({slug}) => slug));

export const WappManualSlugs = new Set(
  entries(WappStackSignals)
    .filter(([, entry]) => entry !== null && 'manualOverride' in entry)
    .map(([slug]) => slug)
);
